import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
    formatPartnerMoney,
    getPartnerStatusLabel,
    getPartnerLifecycleStatus,
    createShopReferral,
    getReusableShopReferralLink,
    getShopReferralLink,
    listShopDashboardRepairRequests,
    listShopReferrals,
    requestShopRepairRequestReschedule,
    saveShopDashboardServiceNotes,
    sendShopDashboardRepairRequestMessage,
    sendShopDashboardEstimate,
    suggestShopDashboardEstimate,
    updateShopDashboardRepairRequestStatus,
    deleteShopDashboardServiceNoteMedia,
    uploadShopDashboardServiceNoteMedia,
} from '../utils/partnerShopApi';
import { formatPhoneLabel, getUserDetailsBaseUrl } from '../utils/chatIdentity';
import { getProfileNameError, normalizeProfileName } from '../utils/profileName';
import { buildPartnerRequestConversationMessages, getPartnerRequestTitle } from '../utils/partnerRequestDisplay';
import { normalizeDiagnosticReportForUi } from '../utils/diagnosticReport';
import { MAX_ODOMETER_MILES, ODOMETER_TOO_HIGH_MESSAGE } from '../utils/distance';
import useShopDashboardRequestUpdates from '../hooks/useShopDashboardRequestUpdates';
import PartnerRequestMessages from './PartnerRequestMessages';
import AppointmentClockPicker from './AppointmentClockPicker';

const STAGE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'new_lead', label: 'New Lead' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'in_service', label: 'In Service' },
    { key: 'ready_for_pickup', label: 'Ready for Pickup' },
];

const JOB_VIEW_TABS = [
    { key: 'active', label: 'Active Jobs' },
    { key: 'completed', label: 'Completed Jobs' },
    { key: 'rejected', label: 'Rejected Jobs' },
];

const ESTIMATE_FILTERS = [
    { key: 'all', label: 'All estimates' },
    { key: 'waiting', label: 'Waiting' },
    { key: 'with_estimate', label: 'Has estimate' },
    { key: 'without_estimate', label: 'No estimate' },
];

const DECLINE_REASON_OPTIONS = [
    { key: 'no_availability', label: 'No availability' },
    { key: 'service_not_offered', label: 'Service not offered' },
    { key: 'unsupported_vehicle', label: 'Unsupported vehicle' },
    { key: 'need_more_information', label: 'Need more information' },
    { key: 'other', label: 'Other' },
];

const STATUS_STYLES = {
    submitted: 'border-[#d8d8d8] bg-[#f6f6f6] text-[#414141]',
    viewed: 'border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]',
    accepted: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]',
    estimate_sent: 'border-[#d8d8d8] bg-[#f6f6f6] text-[#6B6B6B]',
    estimate_accepted: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]',
    scheduled: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]',
    in_service: 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]',
    ready_for_pickup: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]',
    picked_up: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]',
    completed: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]',
    declined: 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]',
    failed: 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]',
    cancelled: 'border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]',
};

const DETAIL_TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'estimate', label: 'Estimate' },
    { key: 'service_notes', label: 'Service Notes' },
    { key: 'messages', label: 'Chat' },
    { key: 'activity', label: 'Activity' },
];

const TERMINAL_STATUSES = ['declined', 'failed', 'cancelled'];
const ACTIVE_REFERRAL_STATUSES = new Set(['link_created', 'link_opened', 'redeemed', 'in_progress']);
const DECLINED_LEAD_STATUSES = new Set(['declined', 'failed', 'cancelled']);
const COMPLETED_LEAD_STATUSES = new Set(['completed', 'picked_up']);
const NEW_LEAD_STAGE_STATUSES = new Set(['submitted', 'viewed', 'accepted', 'estimate_sent', 'estimate_accepted']);

const REFERRAL_STATUS_LABELS = {
    link_created: 'Link sent',
    link_opened: 'Link opened',
    redeemed: 'Customer signed in',
    in_progress: 'Diagnostic started',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
    expired: 'Expired',
    released: 'Released',
    cancelled: 'Cancelled',
    declined: 'Declined',
};


export const WORKFLOW_STEPS = [
    { key: 'submitted', label: 'Request Received' },
    { key: 'estimate_sent', label: 'Awaiting Approval' },
    { key: 'scheduled', label: 'Scheduled', driverOwned: true },
    { key: 'in_service', label: 'In Service' },
    { key: 'ready_for_pickup', label: 'Ready' },
    { key: 'completed', label: 'Completed', driverOwned: true },
];

const ESTIMATE_PRESETS = [
    {
        label: 'Electrical',
        message: 'We reviewed the diagnostic notes and recommend confirming battery health, charging output, and starter circuit before replacing parts.',
        lines: [
            { description: 'Electrical diagnostic and charging system test', amount: 89, line_type: 'diagnostic' },
            { description: 'Battery terminal service', amount: 42, line_type: 'labor' },
            { description: 'Battery replacement allowance if load test fails', amount: 215, line_type: 'parts' },
            { description: 'Shop supplies', amount: 18, line_type: 'other' },
        ],
    },
    {
        label: 'Noise',
        message: 'We reviewed the report and recommend a brake, wheel-end, and suspension inspection before authorizing repairs.',
        lines: [
            { description: 'Brake and suspension inspection', amount: 75, line_type: 'diagnostic' },
            { description: 'Front axle labor allowance', amount: 220, line_type: 'labor' },
            { description: 'Brake or suspension parts allowance', amount: 180, line_type: 'parts' },
            { description: 'Shop supplies', amount: 15, line_type: 'other' },
        ],
    },
    {
        label: 'Cooling / AC',
        message: 'We reviewed the symptoms and recommend pressure, leak, and temperature checks before replacing AC or cooling components.',
        lines: [
            { description: 'AC/cooling system diagnostic', amount: 110, line_type: 'diagnostic' },
            { description: 'Leak inspection and dye test', amount: 95, line_type: 'labor' },
            { description: 'Refrigerant/coolant and parts allowance', amount: 145, line_type: 'parts' },
            { description: 'Recovery and supplies', amount: 28, line_type: 'other' },
        ],
    },
];

const DEFAULT_ESTIMATE_LINES = [
    { line_type: 'diagnostic', description: '', amount: '' },
    { line_type: 'labor', description: '', amount: '' },
    { line_type: 'parts', description: '', amount: '' },
];
const EMPTY_ESTIMATE_LINE = { line_type: 'other', description: '', amount: '', approval_group_label: '' };

const ESTIMATE_MESSAGE_PLACEHOLDER = 'Estimate note for driver: explain what you will inspect, replace, or confirm before repair.';
const ESTIMATE_LINE_DESCRIPTION_PLACEHOLDERS = {
    diagnostic: 'Diagnostic test or inspection',
    labor: 'Labor operation or install work',
    parts: 'Part or material',
    tax: 'Tax, fees, or disposal',
    other: 'Shop supplies or other charge',
};
const ESTIMATE_LINE_TYPE_LABELS = {
    diagnostic: 'Diagnostic',
    labor: 'Labor',
    parts: 'Parts',
    tax: 'Tax',
    other: 'Other',
};

const DEFAULT_SERVICE_PART_FLUID = {
    item_type: 'part',
    name: '',
    brand: '',
    part_number: '',
    quantity: '',
    unit: '',
    notes: '',
};

const DEFAULT_SERVICE_INSPECTION = {
    area: '',
    result: '',
    measurement: '',
    status: '',
    notes: '',
};

const SERVICE_NOTE_TEXT_FIELDS = [
    { key: 'work_performed', label: 'Work performed', rows: 4, placeholder: 'Repairs, tests, adjustments, and verification steps completed' },
    { key: 'confirmed_diagnosis', label: 'Confirmed diagnosis', rows: 3, placeholder: 'Final diagnosis confirmed by the technician' },
    { key: 'root_cause', label: 'Root cause', rows: 3, placeholder: 'Underlying failure or condition that caused the issue' },
    { key: 'future_recommendations', label: 'Future maintenance recommendations', rows: 3, placeholder: 'Recommended maintenance after this visit' },
    { key: 'monitoring_items', label: 'Items requiring monitoring', rows: 3, placeholder: 'Items the owner or shop should keep an eye on' },
    { key: 'estimate_deviations', label: 'Estimate or work-plan deviations', rows: 3, placeholder: 'Changes from the approved estimate or original plan' },
    { key: 'owner_comments', label: 'Owner-facing technician comments', rows: 3, placeholder: 'Comments that should appear in the driver Glove Box record' },
];

const SERVICE_INSPECTION_STATUS_OPTIONS = [
    { value: '', label: 'Status' },
    { value: 'pass', label: 'Pass' },
    { value: 'attention', label: 'Attention' },
    { value: 'monitor', label: 'Monitor' },
    { value: 'fail', label: 'Fail' },
    { value: 'n/a', label: 'N/A' },
];

const SERVICE_MEDIA_PHASE_OPTIONS = [
    { value: 'before', label: 'Before' },
    { value: 'during', label: 'During' },
    { value: 'after', label: 'After' },
];
const MAX_SERVICE_MEDIA_BYTES = 50 * 1024 * 1024;
const SERVICE_MEDIA_IMAGE_EXTENSIONS = new Set(['avif', 'bmp', 'gif', 'heic', 'heif', 'jpeg', 'jfif', 'jpg', 'png', 'tif', 'tiff', 'webp']);
const SERVICE_MEDIA_VIDEO_EXTENSIONS = new Set(['avi', 'm4v', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'webm']);
const SERVICE_MEDIA_ALLOWED_EXTENSIONS = new Set([
    ...SERVICE_MEDIA_IMAGE_EXTENSIONS,
    ...SERVICE_MEDIA_VIDEO_EXTENSIONS,
]);
const SERVICE_MEDIA_SUPPORTED_FILE_TYPES_MESSAGE = 'Please upload one of these file types: .avif, .avi, .bmp, .gif, .heic, .heif, .jpeg, .jfif, .jpg, .m4v, .mkv, .mov, .mp4, .mpeg, .mpg, .png, .tif, .tiff, .webm, or .webp.';
const SERVICE_MEDIA_PREVIEW_IMAGE_EXTENSIONS = new Set(['avif', 'bmp', 'gif', 'jpeg', 'jfif', 'jpg', 'png', 'webp']);
const SERVICE_MEDIA_PREVIEW_IMAGE_MIME_TYPES = new Set(['image/avif', 'image/bmp', 'image/gif', 'image/jpeg', 'image/jfif', 'image/jpg', 'image/pjpeg', 'image/png', 'image/webp']);
const SERVICE_MEDIA_PREVIEW_VIDEO_EXTENSIONS = new Set(['m4v', 'mov', 'mp4', 'webm']);
const SERVICE_MEDIA_PREVIEW_VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v']);
const GUIDED_OUTCOME_LABELS = {
    normal: 'Result was normal / as expected',
    issue: 'Found an issue / result was abnormal',
    not_sure: "Not sure / couldn't complete",
};

function cleanReportText(value = '') {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

function firstReportText(...values) {
    for (const value of values) {
        const cleaned = cleanReportText(value);
        if (cleaned) return cleaned;
    }
    return '';
}

function normalizeReportList(...sources) {
    const values = [];
    sources.forEach((source) => {
        const items = Array.isArray(source) ? source : (source ? [source] : []);
        items.forEach((item) => {
            if (typeof item === 'string') {
                values.push(cleanReportText(item));
                return;
            }
            if (!item || typeof item !== 'object') return;
            values.push(firstReportText(
                item.label,
                item.title,
                item.name,
                item.description,
                item.summary,
                item.recommendedCheck,
                item.check,
                item.cause,
                item.issue,
                item.answer,
                item.text
            ));
        });
    });
    return [...new Set(values.filter(Boolean))];
}

function normalizeCompletedDiagnosticChecks(...sources) {
    return sources
        .flatMap((source) => (Array.isArray(source) ? source : []))
        .map((check) => {
            const outcome = cleanReportText(check?.outcome).toLowerCase();
            const recommendedCheck = firstReportText(
                check?.recommendedCheck,
                check?.checkTitle,
                check?.causeTitle,
                check?.title,
                'Diagnostic check'
            );
            const updatedAt = firstReportText(check?.updatedAt, check?.completedAt, check?.createdAt);

            return {
                id: firstReportText(check?.id, check?.checkId, check?.causeId, `${recommendedCheck}-${updatedAt}`),
                recommendedCheck,
                outcome,
                outcomeLabel: firstReportText(check?.outcomeLabel, GUIDED_OUTCOME_LABELS[outcome]),
                summary: firstReportText(check?.summary, check?.description, check?.causeTitle),
                notes: firstReportText(check?.notes, check?.note),
                updatedAt,
            };
        })
        .filter((check) => check.recommendedCheck);
}

const SHOP_REPORT_SENSITIVE_KEY_FRAGMENTS = ['cost', 'price', 'budget'];
const SHOP_REPORT_SENSITIVE_ESTIMATE_PARTS = ['repair', 'shop', 'parts', 'labor', 'total'];

function isShopReportSensitiveKey(key = '') {
    const normalized = String(key || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (!normalized) return false;
    if (SHOP_REPORT_SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment))) return true;
    return normalized.includes('estimate') && SHOP_REPORT_SENSITIVE_ESTIMATE_PARTS.some((part) => normalized.includes(part));
}

function getShopSafeDiagnosticReport(report) {
    if (!report || typeof report !== 'object') return report;

    function scrub(value) {
        if (Array.isArray(value)) return value.map(scrub);
        if (!value || typeof value !== 'object') return value;
        return Object.fromEntries(
            Object.entries(value)
                .filter(([key]) => !isShopReportSensitiveKey(key))
                .map(([key, item]) => [key, scrub(item)])
        );
    }

    return {
        ...scrub(report),
        shopReadyReport: true,
        hideCostEstimates: true,
        costEstimatesRedacted: true,
    };
}

function getEstimateLineDescriptionPlaceholder(lineType) {
    return ESTIMATE_LINE_DESCRIPTION_PLACEHOLDERS[lineType] || 'Line item description';
}

function getEstimateLineSubmissionDescription(line) {
    const description = String(line?.description || '').trim();
    if (description) return description;
    return ESTIMATE_LINE_TYPE_LABELS[line?.line_type] || ESTIMATE_LINE_TYPE_LABELS.other;
}

const ESTIMATE_AUTHORIZATION_LABELS = {
    pending_customer_approval: 'Pending Customer Approval',
    partially_approved_additional_required: 'Partially Approved – Additional Approval Required',
    partially_approved: 'Partially Approved',
    accepted: 'Accepted',
    declined: 'Declined',
};

export function getEstimateAuthorizationSummary(estimate) {
    const summary = estimate?.authorization_summary;
    if (summary?.line_count != null) {
        return {
            status: summary.status || 'pending_customer_approval',
            label: summary.label || ESTIMATE_AUTHORIZATION_LABELS[summary.status] || 'Pending Customer Approval',
            authorized_amount: Number(summary.authorized_amount || 0),
            additional_approval_amount: Number(summary.additional_approval_amount || summary.pending_amount || 0),
            declined_amount: Number(summary.declined_amount || 0),
            proposed_total: Number(summary.proposed_total ?? estimate?.total_amount ?? 0),
            accepted_count: Number(summary.accepted_count || 0),
            pending_count: Number(summary.pending_count || 0),
            declined_count: Number(summary.declined_count || 0),
            line_count: Number(summary.line_count || 0),
        };
    }

    const status = estimate?.estimate_status === 'accepted'
        ? 'accepted'
        : estimate?.estimate_status === 'declined'
        ? 'declined'
        : 'pending_customer_approval';
    const total = Number(estimate?.total_amount || 0);
    return {
        status,
        label: ESTIMATE_AUTHORIZATION_LABELS[status],
        authorized_amount: status === 'accepted' ? total : 0,
        additional_approval_amount: status === 'pending_customer_approval' ? total : 0,
        declined_amount: status === 'declined' ? total : 0,
        proposed_total: total,
        accepted_count: status === 'accepted' ? 1 : 0,
        pending_count: status === 'pending_customer_approval' ? 1 : 0,
        declined_count: status === 'declined' ? 1 : 0,
        line_count: estimate ? 1 : 0,
    };
}

function getLineAuthorizationStatus(line) {
    const status = String(line?.authorization_status || 'pending').toLowerCase();
    return ['accepted', 'declined', 'pending'].includes(status) ? status : 'pending';
}

function getAuthorizationLineLabel(count, singular, plural = `${singular}s`) {
    return `${count} ${count === 1 ? singular : plural}`;
}

function isEstimateLineAwaitingFreshApproval(line, estimate) {
    return Boolean(
        estimate?.is_update &&
        getLineAuthorizationStatus(line) === 'pending' &&
        !line?.supersedes_estimate_line_id
    );
}

function getAuthorizationTone(status) {
    if (status === 'accepted' || status === 'partially_approved') return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    if (status === 'declined') return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    return 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]';
}

function EstimateAuthorizationBadge({ estimate, summary: explicitSummary }) {
    if (!estimate && !explicitSummary) return null;
    const summary = explicitSummary || getEstimateAuthorizationSummary(estimate);
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getAuthorizationTone(summary.status)}`}>
            {summary.label}
        </span>
    );
}

function EstimateAuthorizationTotals({ estimate }) {
    if (!estimate) return null;
    const summary = getEstimateAuthorizationSummary(estimate);
    return (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2">
                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Already authorized</p>
                <p className="mt-1 text-base font-black text-[#087a43]">{formatPartnerMoney(summary.authorized_amount, estimate.currency)}</p>
                <p className="mt-0.5 text-[11px] font-bold text-[#414141]">
                    {getAuthorizationLineLabel(summary.accepted_count, 'line')} authorized
                </p>
            </div>
            <div className="rounded-xl border border-[#f7d58f] bg-white px-3 py-2">
                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Awaiting approval</p>
                <p className="mt-1 text-base font-black text-[#936000]">{formatPartnerMoney(summary.additional_approval_amount, estimate.currency)}</p>
                <p className="mt-0.5 text-[11px] font-bold text-[#414141]">
                    {getAuthorizationLineLabel(summary.pending_count, 'line')} pending
                </p>
            </div>
            <div className="rounded-xl border border-[#ffd2d2] bg-white px-3 py-2">
                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Declined</p>
                <p className="mt-1 text-base font-black text-[#b42318]">{formatPartnerMoney(summary.declined_amount, estimate.currency)}</p>
                <p className="mt-0.5 text-[11px] font-bold text-[#414141]">
                    {getAuthorizationLineLabel(summary.declined_count, 'line')} declined
                </p>
            </div>
            <div className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2">
                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Proposed total</p>
                <p className="mt-1 text-base font-black text-[#414141]">{formatPartnerMoney(summary.proposed_total, estimate.currency)}</p>
                <p className="mt-0.5 text-[11px] font-bold text-[#414141]">
                    {getAuthorizationLineLabel(summary.line_count, 'line')} tracked
                </p>
            </div>
        </div>
    );
}

function EstimateAuthorizationNotice({ estimate, className = '' }) {
    if (!estimate) return null;
    const summary = getEstimateAuthorizationSummary(estimate);
    const hasAuthorizedPendingWork = summary.accepted_count > 0 && summary.pending_count > 0;
    const hasPendingUpdate = Boolean(estimate.is_update && summary.pending_count > 0);
    if (!hasAuthorizedPendingWork && !hasPendingUpdate) return null;
    const pendingVerb = summary.pending_count === 1 ? 'needs' : 'need';

    return (
        <div className={`${className} rounded-xl border border-[#bde7d5] bg-[#ecfff5] px-3 py-2`}>
            <p className="text-xs font-black text-[#087a43]">
                {hasAuthorizedPendingWork ? 'Authorized work can start' : 'New or changed items need approval'}
            </p>
            <p className="mt-1 text-xs font-semibold leading-relaxed text-[#414141]">
                {hasAuthorizedPendingWork
                    ? `${getAuthorizationLineLabel(summary.accepted_count, 'line')} authorized; ${getAuthorizationLineLabel(summary.pending_count, 'line')} still ${pendingVerb} driver approval.`
                    : `${getAuthorizationLineLabel(summary.pending_count, 'line')} ${pendingVerb} driver approval before that work starts.`}
            </p>
        </div>
    );
}

function EstimateLineAuthorizationChip({ line }) {
    const status = getLineAuthorizationStatus(line);
    const label = status === 'accepted'
        ? 'Authorized'
        : status === 'declined'
        ? 'Declined'
        : 'Awaiting approval';
    return (
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${getAuthorizationTone(status)}`}>
            {label}
        </span>
    );
}

function EstimateDisplayLineRow({ line, estimate, compact = false }) {
    return (
        <div className={`flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between ${compact ? 'py-2' : 'py-3'}`}>
            <div className="min-w-0">
                <span className="font-medium text-[#414141]">{line.description}</span>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <EstimateLineAuthorizationChip line={line} />
                    {isEstimateLineAwaitingFreshApproval(line, estimate) && (
                        <span className="rounded-full border border-[#f7d58f] bg-[#fff8e8] px-2 py-0.5 text-[10px] font-black text-[#936000]">
                            New or changed
                        </span>
                    )}
                </div>
            </div>
            <span className="shrink-0 font-black text-[#414141]">{formatPartnerMoney(line.amount, estimate.currency)}</span>
        </div>
    );
}

function EstimateDisplayLines({ estimate }) {
    const sections = getEstimateLineSections(estimate?.lines || []);
    const groupAmount = (group) => group.items.reduce((sum, item) => sum + Number(item.line?.amount || 0), 0);

    return (
        <div className="mt-3 space-y-3">
            {sections.ungrouped.length > 0 && (
                <div className="divide-y divide-[#ededed]">
                    {sections.ungrouped.map(({ line, index }) => (
                        <EstimateDisplayLineRow
                            key={line.estimate_line_id || `${line.description}-${index}`}
                            line={line}
                            estimate={estimate}
                        />
                    ))}
                </div>
            )}

            {sections.groups.map((group) => (
                <div key={group.key} className="rounded-xl border border-[#d8d8d8] bg-[#F9F9F9]">
                    <div className="flex flex-col gap-2 border-b border-[#d9d9d9] px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                            <p className="text-sm font-black text-[#414141]">{group.label || 'Grouped approval'}</p>
                            <p className="mt-0.5 text-[11px] font-bold text-[#414141]">
                                {getAuthorizationLineLabel(group.items.length, 'line')} approved or declined together
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <span className="rounded-full border border-[#d8d8d8] bg-white px-2 py-0.5 text-[10px] font-black text-[#6B6B6B]">
                                Grouped approval
                            </span>
                            <span className="text-sm font-black text-[#414141]">{formatPartnerMoney(groupAmount(group), estimate.currency)}</span>
                        </div>
                    </div>
                    <div className="divide-y divide-[#ededed] px-3">
                        {group.items.map(({ line, index }) => (
                            <EstimateDisplayLineRow
                                key={line.estimate_line_id || `${line.description}-${index}`}
                                line={line}
                                estimate={estimate}
                                compact
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Icon({ children, className = '' }) {
    return (
        <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            {children}
        </svg>
    );
}

function getVehicleName(request) {
    const vehicle = request?.vehicle || {};
    const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean);
    return parts.length ? parts.join(' ') : request?.shop_ready_summary?.vehicleName || 'Vehicle';
}

function getRequestTitle(request) {
    return getPartnerRequestTitle(request, 'Diagnostic estimate request');
}

export function getTicketLabel(request) {
    const ticketNumber = String(request?.ticket_number || '').trim();
    if (ticketNumber) return `Ticket ${ticketNumber}`;

    const requestId = String(request?.request_id || '').trim();
    return requestId ? `Ticket ${requestId.slice(0, 8)}` : '';
}

function getDeclineReason(request) {
    const notes = String(request?.notes || '').trim();
    if (!notes || /^Customer requested an estimate/i.test(notes)) return '';
    return notes.replace(/^Decline reason:\s*/i, '').trim();
}

function getDeclineReasonLabel(reasonKey) {
    return DECLINE_REASON_OPTIONS.find((option) => option.key === reasonKey)?.label || '';
}

function buildDeclineReasonNote(reasonKey, details = '') {
    const label = getDeclineReasonLabel(reasonKey);
    const cleanDetails = String(details || '').replace(/\s+/g, ' ').trim();
    if (!label) return '';
    return cleanDetails ? `Decline reason: ${label} - ${cleanDetails}` : `Decline reason: ${label}`;
}

function getRequestAge(value) {
    if (!value) return '';
    const created = new Date(value).getTime();
    if (!Number.isFinite(created)) return '';
    const minutes = Math.max(1, Math.round((Date.now() - created) / 60000));
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
}

function getTimestampMs(value) {
    if (!value) return 0;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
}

function getComparableInboxIds(...values) {
    return new Set(values
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean));
}

function setsOverlap(left, right) {
    for (const value of left) {
        if (right.has(value)) return true;
    }
    return false;
}

function getRequestDiagnosticIds(request) {
    const report = request?.report_snapshot || {};
    return getComparableInboxIds(
        request?.diagnostic_ticket_id,
        request?.ticket_number,
        report?.diagnosticTicketId,
        report?.diagnostic_ticket_id,
        report?.ticketNumber,
        report?.ticket_number,
    );
}

function getReferralDiagnosticIds(referral) {
    return getComparableInboxIds(referral?.diagnostic_ticket_id, referral?.ticket_number);
}

function getRequestSourceIds(request) {
    const report = request?.report_snapshot || {};
    return getComparableInboxIds(
        request?.source_repair_log_id,
        request?.repair_log_id,
        request?.thread_id,
        report?.sourceRepairLogId,
        report?.source_repair_log_id,
        report?.repairLogId,
        report?.repair_log_id,
        report?.threadId,
        report?.thread_id,
        report?.conversationId,
        report?.conversation_id,
    );
}

function getReferralSourceIds(referral) {
    return getComparableInboxIds(
        referral?.source_repair_log_id,
        referral?.repair_log_id,
        referral?.thread_id,
    );
}

function getRequestShopId(request) {
    return String(request?.partner_shop_id || request?.partner_shop?.partner_shop_id || request?.partner_shop?.id || request?.shop_id || '').trim();
}

function getReferralShopId(referral) {
    return String(referral?.partner_shop_id || referral?.shop_id || '').trim();
}

function referralMatchesConvertedLead(referral, request) {
    const referralShopId = getReferralShopId(referral);
    const requestShopId = getRequestShopId(request);
    if (referralShopId && requestShopId && referralShopId !== requestShopId) return false;
    if (referral?.redeemed_user_id && request?.source_user_id
        && String(referral.redeemed_user_id) !== String(request.source_user_id)) return false;
    if (request?.referral?.referral_id) return String(request.referral.referral_id) === String(referral.referral_id);
    if (referral?.linked_request_id) return String(referral.linked_request_id) === String(request.request_id);
    if (setsOverlap(getReferralDiagnosticIds(referral), getRequestDiagnosticIds(request))) return true;
    const sourceIds = getReferralSourceIds(referral);
    return sourceIds.size > 0 && setsOverlap(sourceIds, getRequestSourceIds(request));
}

function getReferralStatusLabel(referral) {
    const status = String(referral?.status || '').toLowerCase();
    return referral?.intake_progress?.label || REFERRAL_STATUS_LABELS[status] || 'Intake in progress';
}

// A customer email on "@carfido.com" is a fabricated placeholder from the old report
// generator, never a real driver address — never show it to a shop.
const isPlaceholderCustomerEmail = (email = '') => /@carfido\.com\s*$/i.test(String(email || '').trim());
// First real (non-placeholder) email among the candidates; '' if none.
const pickRealCustomerEmail = (...candidates) => {
    for (const c of candidates) {
        const e = String(c || '').trim();
        if (e && !isPlaceholderCustomerEmail(e)) return e;
    }
    return '';
};

function getReferralCustomerName(referral) {
    const name = String(referral?.customer_name || '').trim();
    if (name) return name;
    const phone = String(referral?.customer_phone || '').trim();
    if (phone) return formatPhoneLabel(phone);
    return pickRealCustomerEmail(referral?.customer_email) || 'Customer';
}

function getReferralIssueTitle(referral) {
    return String(referral?.issue_title || referral?.preliminary_issue || '').trim() || 'Shop referral intake';
}

function getReferralCreatedAge(referral) {
    return getRequestAge(referral?.created_at);
}

function getJobStageFilterBucket(request) {
    const status = String(request?.request_status || '').toLowerCase();
    if (DECLINED_LEAD_STATUSES.has(status) || COMPLETED_LEAD_STATUSES.has(status)) return '';
    if (status === 'ready_for_pickup') return 'ready_for_pickup';
    if (status === 'in_service') return 'in_service';
    if (status === 'scheduled') return 'scheduled';
    if (request?.scheduled_start_at) return 'scheduled';
    if (NEW_LEAD_STAGE_STATUSES.has(status) || request?.latest_estimate || !status) return 'new_lead';
    return '';
}

function getJobViewBucket(request) {
    const status = String(request?.request_status || '').toLowerCase();
    if (COMPLETED_LEAD_STATUSES.has(status)) return 'completed';
    if (DECLINED_LEAD_STATUSES.has(status)) return 'rejected';
    return 'active';
}

function requestMatchesStatusFilter(request, filter, jobView = 'active') {
    if (getJobViewBucket(request) !== jobView) return false;
    if (jobView !== 'active') return true;
    if (!filter || filter === 'all') return true;
    return getJobStageFilterBucket(request) === filter;
}

export function getCustomerName(request) {
    const displayName = String(request?.customer?.display_name || '').trim();
    if (displayName && displayName.toLowerCase() !== 'dash driver') return displayName;

    const phone = String(request?.customer?.phone_number || '').trim();
    return phone ? formatPhoneLabel(phone) : 'Customer';
}

export function getCustomerPhoneLabel(request) {
    const phone = String(request?.customer?.phone_number || '').trim();
    return phone ? formatPhoneLabel(phone) : 'Phone unavailable';
}

function getReportIdForRequest(request, report = {}) {
    const explicitId = firstReportText(report.id, report.reportId, report.diagnosticReportId);
    if (explicitId) return explicitId;

    const ticketId = firstReportText(report.diagnosticTicketId, report.diagnostic_ticket_id, request?.diagnostic_ticket_id);
    if (ticketId) return `ticket-${ticketId}`;

    const ticketNumber = firstReportText(report.ticketNumber, report.ticket_number, request?.ticket_number);
    if (ticketNumber) return `ticket-${ticketNumber}`;

    const requestId = firstReportText(request?.request_id);
    return requestId ? `partner-request-${requestId}` : '';
}

function getDetailedReportForRequest(request) {
    const source = getShopSafeDiagnosticReport(request?.report_snapshot);
    if (!source || typeof source !== 'object' || Object.keys(source).length === 0) return null;

    const summary = request?.shop_ready_summary || {};
    const service = request?.services?.[0] || {};
    const reportId = getReportIdForRequest(request, source);
    if (!reportId) return null;

    return normalizeDiagnosticReportForUi({
        ...source,
        id: reportId,
        diagnosticTicketId: firstReportText(source.diagnosticTicketId, source.diagnostic_ticket_id, request?.diagnostic_ticket_id),
        ticketNumber: firstReportText(source.ticketNumber, source.ticket_number, request?.ticket_number),
        vehicle: firstReportText(source.vehicle, summary.vehicleName, getVehicleName(request)),
        customerName: firstReportText(source.customerName, source.customer_name, getCustomerName(request)),
        customerPhone: firstReportText(source.customerPhone, source.customer_phone, request?.customer?.phone_number),
        // Prefer the REAL account email on the request over the report snapshot's copy, and
        // never surface an "@carfido.com" placeholder baked into an older snapshot.
        customerEmail: pickRealCustomerEmail(request?.customer?.email, source.customer_email, source.customerEmail),
        customerComplaint: firstReportText(
            source.customerComplaint,
            source.customer_complaint,
            summary.customerComplaint,
            source.mechanicSummary,
            source.issue,
            service.summary,
            service.title
        ),
    });
}

export function getShopReadyReportOverview(request) {
    const report = getDetailedReportForRequest(request);
    const summary = request?.shop_ready_summary || {};
    const service = request?.services?.[0] || {};
    const symptoms = normalizeReportList(
        summary.observedSymptoms,
        report?.symptomsDetailed,
        report?.symptoms,
        report?.observedSymptoms
    );
    const likelyCauses = normalizeReportList(
        summary.likelyCauses,
        service.likely_causes,
        service.likelyCauses,
        report?.likelyCauses
    );
    const recommendedChecks = normalizeReportList(
        summary.requestedTests,
        service.requested_tests,
        service.requestedTests,
        report?.recommendedChecks
    );
    const completedChecks = normalizeCompletedDiagnosticChecks(
        summary.completedDiagnosticChecks,
        report?.completedGuidedChecks
    );

    return {
        detailedReport: report,
        customerName: getCustomerName(request),
        customerPhone: getCustomerPhoneLabel(request),
        customerEmail: firstReportText(request?.customer?.email, report?.customerEmail, 'Email unavailable'),
        vehicleName: firstReportText(report?.vehicle, summary.vehicleName, getVehicleName(request)),
        customerComplaint: firstReportText(
            summary.customerComplaint,
            report?.customerComplaint,
            report?.mechanicSummary,
            report?.issue,
            service.summary,
            service.title,
            'No report summary available.'
        ),
        symptoms,
        likelyCauses,
        completedChecks,
        recommendedChecks,
    };
}

function formatAppointment(request) {
    if (!request?.scheduled_start_at) return '';
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: request.scheduled_timezone || 'America/Denver',
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(new Date(request.scheduled_start_at));
    } catch (_) {
        return new Date(request.scheduled_start_at).toLocaleString();
    }
}

function hasDriverApprovedEstimate(request) {
    const summary = getEstimateAuthorizationSummary(request?.latest_estimate);
    return Boolean(
        ['accepted', 'partially_approved'].includes(summary.status) ||
        request?.latest_estimate?.estimate_status === 'accepted' ||
        ['estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed'].includes(request?.request_status)
    );
}

function hasAnyDriverAuthorizedWork(request) {
    const summary = getEstimateAuthorizationSummary(request?.latest_estimate);
    return Boolean(
        summary.accepted_count > 0 ||
        ['accepted', 'partially_approved', 'partially_approved_additional_required'].includes(summary.status) ||
        request?.latest_estimate?.estimate_status === 'accepted' ||
        ['estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed'].includes(request?.request_status)
    );
}

function hasScheduledAppointment(request) {
    return Boolean(
        request?.scheduled_start_at ||
        ['scheduled', 'in_service', 'ready_for_pickup', 'picked_up', 'completed'].includes(request?.request_status)
    );
}

export function getLeadWorkflow(request) {
    const status = request?.request_status || 'submitted';
    const terminal = TERMINAL_STATUSES.includes(status);
    const hasEstimate = Boolean(request?.latest_estimate);
    const authorizationSummary = getEstimateAuthorizationSummary(request?.latest_estimate);
    const driverApproved = hasDriverApprovedEstimate(request);
    const scheduled = hasScheduledAppointment(request);

    if (terminal) {
        return {
            currentKey: '',
            waitingKey: '',
            doneThroughKey: '',
            terminal: true,
            terminalLabel: getPartnerStatusLabel(status, 'shop'),
            nextLabel: `Closed: ${getPartnerStatusLabel(status, 'shop')}`,
            nextDescription: 'No shop action is available for this lead.',
            action: null,
        };
    }

    if (status === 'picked_up') {
        return {
            currentKey: '',
            waitingKey: '',
            doneThroughKey: 'completed',
            terminal: false,
            nextLabel: 'Completed',
            nextDescription: 'The driver confirmed vehicle pickup and the service record is complete.',
            action: null,
        };
    }

    if (status === 'completed') {
        return {
            currentKey: '',
            waitingKey: '',
            doneThroughKey: 'completed',
            terminal: false,
            nextLabel: 'Completed',
            nextDescription: 'The vehicle was picked up and the service record is complete.',
            action: null,
        };
    }

    if (status === 'ready_for_pickup') {
        return {
            currentKey: '',
            waitingKey: 'completed',
            doneThroughKey: 'ready_for_pickup',
            terminal: false,
            nextLabel: 'Waiting for completion',
            nextDescription: 'Vehicle is ready. The driver can confirm pickup in the chat.',
            action: { type: 'status', status: 'picked_up', label: 'Mark completed' },
        };
    }

    if (status === 'in_service') {
        return {
            currentKey: 'in_service',
            waitingKey: '',
            doneThroughKey: 'scheduled',
            terminal: false,
            nextLabel: 'Ready for pickup',
            nextDescription: 'Vehicle is in service. Mark it ready when the work is finished.',
            action: { type: 'status', status: 'ready_for_pickup', label: 'Ready for pickup' },
        };
    }

    if (scheduled || status === 'scheduled') {
        return {
            currentKey: 'scheduled',
            waitingKey: '',
            doneThroughKey: 'estimate_sent',
            terminal: false,
            nextLabel: 'Start service',
            nextDescription: 'Appointment is scheduled. Move the lead into service at drop-off.',
            action: { type: 'status', status: 'in_service', label: 'Start service' },
        };
    }

    if (driverApproved || status === 'estimate_accepted') {
        return {
            currentKey: 'scheduled',
            waitingKey: 'scheduled',
            doneThroughKey: 'estimate_sent',
            terminal: false,
            nextLabel: 'Waiting for appointment',
            nextDescription: 'Driver approved the estimate. Use chat if the drop-off time needs coordination.',
            action: { type: 'tab', tab: 'messages', label: 'Message driver' },
        };
    }

    if (hasEstimate || status === 'estimate_sent') {
        const partialAdditional = authorizationSummary.status === 'partially_approved_additional_required';
        return {
            currentKey: 'estimate_sent',
            waitingKey: 'estimate_sent',
            doneThroughKey: 'submitted',
            terminal: false,
            nextLabel: partialAdditional ? 'Additional approval required' : 'Waiting for driver approval',
            nextDescription: partialAdditional
                ? 'Unchanged items remain authorized. Added or modified items still need driver approval.'
                : 'Estimate sent. The driver needs to approve before scheduling.',
            action: { type: 'tab', tab: 'messages', label: 'Message driver' },
        };
    }

    if (status === 'accepted') {
        return {
            currentKey: 'estimate_sent',
            waitingKey: '',
            doneThroughKey: 'submitted',
            terminal: false,
            nextLabel: 'Build estimate',
            nextDescription: 'Request received. Send an itemized estimate to the driver.',
            action: { type: 'tab', tab: 'estimate', label: 'Build estimate' },
        };
    }

    return {
        currentKey: 'submitted',
        waitingKey: '',
        doneThroughKey: '',
        terminal: false,
        nextLabel: 'Build estimate',
        nextDescription: 'Request received. Review it and send an itemized estimate.',
        action: { type: 'tab', tab: 'estimate', label: 'Build estimate' },
    };
}

function getStepIndex(key) {
    return WORKFLOW_STEPS.findIndex((step) => step.key === key);
}

export function getWorkflowStepState(step, workflow) {
    const stepIndex = getStepIndex(step.key);
    const doneThroughIndex = getStepIndex(workflow.doneThroughKey);
    const currentIndex = getStepIndex(workflow.currentKey);

    if (doneThroughIndex >= stepIndex && doneThroughIndex !== -1) return 'done';
    if (workflow.waitingKey === step.key) return 'waiting';
    if (currentIndex === stepIndex && currentIndex !== -1) return 'current';
    return 'locked';
}

function StatusBadge({ status }) {
    const lifecycleStatus = getPartnerLifecycleStatus(status);
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[lifecycleStatus] || STATUS_STYLES.submitted}`}>
            {getPartnerStatusLabel(status, 'shop')}
        </span>
    );
}

function SmallMetric({ label, value, emphasis = false }) {
    return (
        <div
            role="group"
            aria-label={`${label}: ${value}`}
            className={`rounded-xl border px-3 py-2 text-center shadow-sm ${
                emphasis
                    ? 'border-[#d8d8d8] bg-[#f6f6f6]'
                    : 'border-[#d9d9d9] bg-white'
            }`}
        >
            <p className={`text-[10px] font-black uppercase tracking-[0.08em] ${emphasis ? 'text-[#414141]' : 'text-[#414141]'}`}>{label}</p>
            <p className={`mt-0.5 font-black ${emphasis ? 'text-xl text-[#414141]' : 'text-lg text-[#414141]'}`}>{value}</p>
        </div>
    );
}

function RequestRow({ request, active, onClick }) {
    const estimate = request.latest_estimate;
    const authorizationSummary = getEstimateAuthorizationSummary(estimate);
    const newLead = request.request_status === 'submitted';
    const title = getRequestTitle(request);
    const ticketLabel = getTicketLabel(request);
    const vehicleName = getVehicleName(request);
    const messageCount = buildPartnerRequestConversationMessages(request, { driverLabel: 'Driver' }).length;
    const workflow = getLeadWorkflow(request);

    return (
        <button
            type="button"
            onClick={() => onClick(request)}
            aria-label={`Open lead: ${title}`}
            className={`group w-full rounded-2xl border bg-white px-4 py-3.5 text-left shadow-sm transition md:px-5 ${
                active
                    ? 'border-[#D81F26] shadow-sm ring-1 ring-[#D81F26]/15'
                    : 'border-[#d9d9d9] hover:border-[#414141] hover:bg-[#F9F9F9]'
            }`}
        >
            <div className="flex items-start gap-3">
                <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition ${
                    newLead
                        ? 'border-[#F5C2C4] bg-[#FDEDEE] text-[#D81F26]'
                        : workflow.terminal
                        ? 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                        : 'border-[#d9d9d9] bg-[#F9F9F9] text-[#414141] group-hover:border-[#B3B3B3] group-hover:bg-[#f5f5f5]'
                }`}>
                    <Icon className="h-5 w-5">
                        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.4-.6-.6-2.4z" />
                    </Icon>
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="line-clamp-2 text-sm font-black leading-snug text-black">{title}</p>
                            <p className="mt-1 truncate text-xs font-semibold text-[#414141]">
                                {getCustomerName(request)} / {vehicleName}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1 text-[10px] font-bold text-[#414141]">
                            <span>{getRequestAge(request.referral?.created_at || request.created_at)}</span>
                            <Icon className="h-3.5 w-3.5"><path d="m9 18 6-6-6-6" /></Icon>
                        </div>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={request.request_status} />
                        {ticketLabel && (
                            <span className="rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-2 py-0.5 text-[10px] font-semibold text-[#414141]">
                                {ticketLabel}
                            </span>
                        )}
                        <span className="rounded-full border border-[#d9d9d9] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#414141]">
                            {estimate ? formatPartnerMoney(authorizationSummary.proposed_total, estimate.currency) : 'No estimate'}
                        </span>
                        {estimate && <EstimateAuthorizationBadge summary={authorizationSummary} />}
                        {authorizationSummary.accepted_count > 0 && authorizationSummary.pending_count > 0 && (
                            <span className="rounded-full border border-[#bde7d5] bg-[#ecfff5] px-2 py-0.5 text-[10px] font-semibold text-[#087a43]">
                                Authorized work
                            </span>
                        )}
                        {messageCount > 0 && (
                            <span className="rounded-full border border-[#d8d8d8] bg-[#f6f6f6] px-2 py-0.5 text-[10px] font-semibold text-[#414141]">
                                {messageCount} msg
                            </span>
                        )}
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-[#d9d9d9] pt-2 text-[11px] font-semibold text-[#414141]">
                        <span className="min-w-0 truncate">
                            <span className="font-black text-[#414141]">Next:</span> {workflow.nextLabel}
                        </span>
                        <span className="truncate">{request.partner_shop?.name || 'Partner shop'}</span>
                    </div>
                    {request.referral && (
                        <div className="mt-3 border-t border-[#d9d9d9] pt-2">
                            {!request.scheduled_start_at && request.referral.appointment_date && (
                                <p className="mb-2 text-xs font-bold text-[#087a43]">{request.referral.appointment_date}</p>
                            )}
                            <ReferralProgress referral={request.referral} completed={COMPLETED_LEAD_STATUSES.has(request.request_status)} />
                        </div>
                    )}
                    {request.scheduled_start_at && (
                        <div className="mt-2 flex items-center gap-1.5 border-t border-[#ededed] pt-2 text-[11px] font-black text-[#087a43]">
                            <Icon className="h-3.5 w-3.5"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></Icon>
                            <span className="truncate">{formatAppointment(request)}</span>
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
}

function ReferralProgress({ referral, completed = false }) {
    const progress = referral?.intake_progress || {};
    const percent = completed ? 100 : Math.max(0, Math.min(100, Number(progress.percent || 0)));
    const steps = Array.isArray(progress.steps) ? progress.steps : [];
    return (
        <div>
            <div role="progressbar" aria-label={`Intake progress for ${referral.issue_title || 'referral job'}`}
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
                className="h-2 overflow-hidden rounded-full bg-[#d9d9d9]">
                <div className="h-full rounded-full bg-[#D81F26]" style={{ width: `${percent}%` }} />
            </div>
            <div className="mt-2 grid gap-1 text-[10px] font-black uppercase tracking-[0.06em] text-[#414141] sm:grid-cols-4">
                {steps.map((step) => <span key={step.key} className={step.done || completed ? 'text-[#087a43]' : ''}>{step.label}</span>)}
            </div>
        </div>
    );
}

function ReferralIntakeRow({ referral, linkedRequest = null, onCopyLink, isCopying = false, copied = false }) {
    const linkedLeadStatus = String(linkedRequest?.request_status || '').toLowerCase();
    const linkedLeadCompleted = COMPLETED_LEAD_STATUSES.has(linkedLeadStatus);
    const appointment = String(referral?.appointment_date || '').trim();
    const reference = String(referral?.shop_reference_number || '').trim();
    const age = getReferralCreatedAge(referral);
    const linkedTicketLabel = linkedRequest ? getTicketLabel(linkedRequest) : '';
    const statusLabel = linkedLeadCompleted ? getPartnerStatusLabel(linkedRequest.request_status, 'shop') : getReferralStatusLabel(referral);
    const statusTone = linkedLeadCompleted
        ? 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'
        : 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]';

    return (
        <div className="group w-full rounded-2xl border border-[#d9d9d9] bg-white px-4 py-3.5 text-left shadow-sm transition md:px-5">
            <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#f7d58f] bg-[#fff8e8] text-[#936000]">
                    <Icon className="h-5 w-5"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Icon>
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="line-clamp-2 text-sm font-black leading-snug text-black">{getReferralIssueTitle(referral)}</p>
                            <p className="mt-1 truncate text-xs font-semibold text-[#414141]">
                                {getReferralCustomerName(referral)}
                                {appointment ? ` / ${appointment}` : ''}
                                {reference ? ` / Ref ${reference}` : ''}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1 text-[10px] font-bold text-[#414141]">
                            <span>{age}</span>
                        </div>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-2 py-0.5 text-[10px] font-semibold text-[#414141]">
                            Intake
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusTone}`}>
                            {statusLabel}
                        </span>
                        {linkedTicketLabel && (
                            <span className="rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-2 py-0.5 text-[10px] font-semibold text-[#414141]">
                                {linkedTicketLabel}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => onCopyLink?.(referral)}
                            disabled={isCopying}
                            className="ml-auto inline-flex min-h-[24px] items-center rounded-full border border-[#d9d9d9] bg-white px-2.5 text-[10px] font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26] disabled:cursor-wait disabled:opacity-60"
                        >
                            {isCopying ? 'Copying' : copied ? 'Copied' : 'Copy link'}
                        </button>
                    </div>

                    <div className="mt-3"><ReferralProgress referral={referral} completed={linkedLeadCompleted} /></div>
                </div>
            </div>
        </div>
    );
}

function StatusProcess({ request, isUpdating, onStatus, onOpenTab }) {
    const currentStatus = request?.request_status || 'submitted';
    const workflow = getLeadWorkflow(request);

    if (workflow.terminal) {
        return (
            <div className="rounded-2xl border border-[#ffd2d2] bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff1f1] text-[#b42318]">
                            <Icon className="h-5 w-5"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>
                        </span>
                        <div>
                            <p className="text-sm font-black text-[#414141]">Lead is {workflow.terminalLabel}</p>
                            <p className="mt-1 text-sm font-medium text-[#414141]">{workflow.nextDescription}</p>
                        </div>
                    </div>
                    <StatusBadge status={currentStatus} />
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-[#d9d9d9] bg-white p-3 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">Workflow</p>
                    <p className="mt-0.5 line-clamp-2 text-xs font-medium leading-snug text-[#414141]">
                        <span className="font-black text-[#414141]">Next:</span> {workflow.nextDescription}
                    </p>
                </div>
                {!['completed', 'picked_up'].includes(currentStatus) && (() => {
                    // Once the driver has APPROVED the estimate, the work is authorized and
                    // moving forward — the shop can no longer decline the lead. Disable the
                    // button (kept visible, greyed) so the state is clear.
                    const estimateApproved = hasAnyDriverAuthorizedWork(request);
                    return (
                        <button
                            type="button"
                            disabled={isUpdating || estimateApproved}
                            onClick={() => onStatus('declined')}
                            title={estimateApproved ? 'The driver has approved the estimate — this lead can no longer be declined.' : undefined}
                            className="shrink-0 rounded-full border border-[#ffd2d2] bg-white px-3 py-1.5 text-xs font-black text-[#b42318] transition hover:bg-[#fff1f1] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Decline
                        </button>
                    );
                })()}
            </div>
            <div className="mt-3 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                {WORKFLOW_STEPS.map((step, index) => {
                    const state = getWorkflowStepState(step, workflow);
                    const activeAction = workflow.action && workflow.currentKey === step.key;
                    const waitingAction = workflow.action?.type === 'tab' && workflow.waitingKey === step.key;
                    const canClick = !isUpdating && Boolean(activeAction || waitingAction);
                    const helper = state === 'done'
                        ? 'Done'
                        : state === 'waiting'
                        ? waitingAction ? workflow.action.label : (step.key === 'estimate_sent' ? 'Waiting for driver' : 'Waiting on driver')
                        : state === 'current'
                        ? workflow.action?.label || 'Current'
                        : step.driverOwned
                        ? 'Driver step'
                        : 'Locked';
                    const stateClass = state === 'done'
                        ? 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'
                        : state === 'waiting'
                        ? 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]'
                        : state === 'current'
                        ? 'border-[#c7c7c7] bg-[#f8f8f8] text-[#D81F26] ring-1 ring-[#d8d8d8]'
                        : 'border-[#ededed] bg-[#F9F9F9] text-[#9e9e9e]';

                    return (
                        <button
                            key={step.key}
                            type="button"
                            disabled={!canClick}
                            onClick={() => {
                                if (!workflow.action) return;
                                if (workflow.action.type === 'tab') {
                                    onOpenTab(workflow.action.tab);
                                    return;
                                }
                                onStatus(workflow.action.status);
                            }}
                            className={`min-h-[58px] rounded-xl border px-2.5 py-2 text-left transition ${stateClass} ${
                                canClick ? 'hover:border-[#414141] hover:bg-white' : ''
                            } disabled:cursor-not-allowed`}
                        >
                            <div className="flex items-center gap-1.5">
                                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-black ${
                                    state === 'current'
                                        ? 'bg-[#D81F26] text-white'
                                        : state === 'done'
                                        ? 'bg-[#087a43] text-white'
                                        : state === 'waiting'
                                        ? 'bg-[#936000] text-white'
                                        : 'bg-white text-[#414141] ring-1 ring-[#d9d9d9]'
                                }`}>
                                    {state === 'done' ? (
                                        <Icon className="h-2.5 w-2.5"><path d="M20 6 9 17l-5-5" /></Icon>
                                    ) : (
                                        index + 1
                                    )}
                                </span>
                                <span className="min-w-0 whitespace-normal text-[11px] font-black leading-tight">{step.label}</span>
                            </div>
                            <p className="mt-1 line-clamp-1 text-[9px] font-black uppercase tracking-[0.06em] opacity-75">{isUpdating && canClick ? 'Updating' : helper}</p>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function DeclineLeadModal({
    request,
    reason,
    details,
    error,
    isSubmitting,
    onReasonChange,
    onDetailsChange,
    onCancel,
    onSubmit,
}) {
    if (!request) return null;
    const title = getRequestTitle(request);

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-4 py-4 sm:items-center">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-2xl">
                <div className="border-b border-[#d9d9d9] px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#b42318]">Decline lead</p>
                            <h2 className="mt-1 line-clamp-2 text-lg font-black text-[#414141]">{title}</h2>
                            <p className="mt-1 text-sm font-semibold text-[#414141]">{getCustomerName(request)} / {getVehicleName(request)}</p>
                        </div>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            aria-label="Close decline lead"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#d9d9d9] text-[#414141] transition hover:bg-[#F9F9F9] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <Icon className="h-4 w-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>
                        </button>
                    </div>
                </div>
                <div className="space-y-4 px-5 py-4">
                    <fieldset>
                        <legend className="text-xs font-black uppercase tracking-[0.08em] text-[#414141]">Reason</legend>
                        <div className="mt-2 grid gap-2">
                            {DECLINE_REASON_OPTIONS.map((option) => (
                                <label
                                    key={option.key}
                                    className={`flex min-h-[42px] cursor-pointer items-center gap-3 rounded-xl border px-3 text-sm font-black transition ${
                                        reason === option.key
                                            ? 'border-[#b42318] bg-[#fff1f1] text-[#b42318]'
                                            : 'border-[#d9d9d9] bg-white text-[#414141] hover:border-[#414141] hover:bg-[#F9F9F9]'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="decline-reason"
                                        value={option.key}
                                        checked={reason === option.key}
                                        onChange={() => onReasonChange(option.key)}
                                        className="h-4 w-4 accent-[#b42318]"
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                    </fieldset>
                    <label className="block">
                        <span className="text-xs font-black uppercase tracking-[0.08em] text-[#414141]">Optional detail</span>
                        <textarea
                            value={details}
                            onChange={(event) => onDetailsChange(event.target.value)}
                            placeholder="Add a short note for the customer or CarFido"
                            rows={3}
                            maxLength={900}
                            className="mt-2 min-h-[92px] w-full resize-y rounded-xl border border-[#d9d9d9] bg-white px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#b42318]"
                        />
                    </label>
                    {error && (
                        <div className="rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-black text-[#b42318]">
                            {error}
                        </div>
                    )}
                </div>
                <div className="flex flex-col-reverse gap-2 border-t border-[#d9d9d9] px-5 py-4 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={isSubmitting}
                        className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#b42318] px-5 text-sm font-black text-white transition hover:bg-[#981b14] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSubmitting ? 'Declining...' : 'Decline lead'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DetailSection({ title, children, className = '' }) {
    return (
        <section className={`rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm ${className}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">{title}</p>
            <div className="mt-3">{children}</div>
        </section>
    );
}

const formatServiceHistoryDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatServiceHistoryMileage = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const miles = Number(value);
    if (!Number.isFinite(miles)) return '';
    return `${miles.toLocaleString()} mi`;
};

function ServiceHistoryPanel({ entries = [] }) {
    const visibleEntries = Array.isArray(entries) ? entries.filter((entry) => entry && !entry.is_voided) : [];
    if (!visibleEntries.length) {
        return (
            <div className="rounded-2xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4 text-sm font-bold text-[#414141]">
                No vehicle service history shared for this job yet.
            </div>
        );
    }
    return (
        <div className="space-y-2.5">
            {visibleEntries.map((entry, index) => {
                const title = entry.title || 'Service record';
                const dateLabel = formatServiceHistoryDate(entry.performed_at);
                const mileageLabel = formatServiceHistoryMileage(entry.odometer_mileage);
                const provider = entry.shop_name || entry.performed_by || '';
                const costLabel = entry.invoice_total !== null && entry.invoice_total !== undefined
                    ? formatPartnerMoney(entry.invoice_total)
                    : '';
                return (
                    <article key={entry.service_event_id || `${title}-${index}`} className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h4 className="text-sm font-black text-[#414141]">{title}</h4>
                                    {entry.amended && (
                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-700">Amended</span>
                                    )}
                                </div>
                                <p className="mt-1 text-xs font-bold text-[#414141]">
                                    {[dateLabel, mileageLabel, provider, costLabel].filter(Boolean).join(' / ') || 'Service details saved'}
                                </p>
                            </div>
                        </div>
                        {entry.notes && (
                            <p className="mt-2 whitespace-pre-wrap break-words text-xs font-medium leading-relaxed text-[#414141]">{entry.notes}</p>
                        )}
                    </article>
                );
            })}
        </div>
    );
}

function Timeline({ items = [] }) {
    if (!items.length) {
        return <p className="text-sm font-medium text-[#414141]">No activity yet.</p>;
    }

    return (
        <div className="space-y-3">
            {items.map((item, index) => (
                <div key={`${item.event}-${item.created_at}-${index}`} className="flex gap-3">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#8C8C8C]" />
                    <div>
                        <p className="text-sm font-black text-[#414141]">{item.label || getPartnerStatusLabel(item.status, 'shop')}</p>
                        <p className="text-xs font-medium text-[#414141]">{item.created_at ? new Date(item.created_at).toLocaleString() : 'Time unavailable'}</p>
                        {item.error_message && <p className="mt-1 text-xs font-medium text-red-700">{item.error_message}</p>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ListItems({ items = [], emptyText = 'None captured.' }) {
    if (!items.length) return <p className="text-sm font-medium text-[#414141]">{emptyText}</p>;
    return (
        <ul className="space-y-1.5">
            {items.map((item) => (
                <li key={item} className="flex gap-2 text-sm font-medium leading-snug text-[#414141]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8C8C8C]" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

function getServiceMediaKind(item = {}) {
    const mediaKind = String(item.media_kind || '').toLowerCase();
    const mimeType = String(item.mime_type || '').toLowerCase();
    const filename = item.filename || item.original_filename || item.originalFilename || item.name || '';
    const extension = getUploadFileExtension(filename);
    if (mediaKind === 'video' || mimeType.startsWith('video/') || SERVICE_MEDIA_VIDEO_EXTENSIONS.has(extension)) return 'video';
    if (
        mediaKind === 'photo'
        || mediaKind === 'image'
        || mimeType.startsWith('image/')
        || SERVICE_MEDIA_IMAGE_EXTENSIONS.has(extension)
    ) {
        return 'photo';
    }
    return 'file';
}

function isServiceMediaPreviewable(item = {}) {
    const kind = getServiceMediaKind(item);
    const mimeType = String(item.mime_type || '').toLowerCase();
    const filename = item.filename || item.original_filename || item.originalFilename || item.name || '';
    const extension = getUploadFileExtension(filename);
    if (kind === 'video') {
        return SERVICE_MEDIA_PREVIEW_VIDEO_MIME_TYPES.has(mimeType) || SERVICE_MEDIA_PREVIEW_VIDEO_EXTENSIONS.has(extension);
    }
    if (kind === 'photo') {
        return SERVICE_MEDIA_PREVIEW_IMAGE_MIME_TYPES.has(mimeType) || SERVICE_MEDIA_PREVIEW_IMAGE_EXTENSIONS.has(extension);
    }
    return false;
}

function getIntakeMediaKind(item = {}) {
    const kind = getServiceMediaKind(item);
    return kind === 'video' ? 'video' : 'photo';
}

function getServiceMediaUrl(item = {}) {
    return item.url || item.preview_url || item.display_url || item.file_url || item.storage_url || '';
}

function ServiceMediaPreviewModal({ item, onClose }) {
    if (!item) return null;
    const src = getServiceMediaUrl(item);
    if (!src) return null;
    const kind = getServiceMediaKind(item);
    const title = item.filename || (kind === 'video' ? 'Service video' : 'Service photo');

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 px-4 py-6" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="relative max-h-full w-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
                    aria-label="Close preview"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>
                {kind === 'video' ? (
                    <video src={src} controls className="mx-auto max-h-[82vh] max-w-full rounded-2xl bg-black shadow-2xl" aria-label={title} />
                ) : (
                    <img src={src} alt={title} className="mx-auto max-h-[82vh] max-w-full rounded-2xl bg-white object-contain shadow-2xl" />
                )}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
                    <p className="min-w-0 truncate text-sm font-black text-[#414141]">{title}</p>
                    <a href={src} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-full bg-[#D81F26] px-4 py-2 text-xs font-black text-white no-underline hover:bg-[#B8181E]">
                        Open in new tab
                    </a>
                </div>
            </div>
        </div>
    );
}

function IntakeMediaPanel({ media = [] }) {
    const [previewMedia, setPreviewMedia] = useState(null);

    if (!media.length) {
        return <p className="text-sm font-medium text-[#414141]">No customer media was attached during intake.</p>;
    }

    function handleOpenMedia(item) {
        const url = getServiceMediaUrl(item);
        if (!url) return;
        if (isServiceMediaPreviewable(item)) {
            setPreviewMedia(item);
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    return (
        <>
            <div className="divide-y divide-[#d9d9d9]">
                {media.map((item, index) => {
                    const kind = getIntakeMediaKind(item);
                    const label = kind === 'video' ? 'Video' : 'Photo';
                    const title = item.filename || `${label} evidence`;
                    const key = item.attachment_id || item.doc_id || `${title}-${index}`;
                    const url = getServiceMediaUrl(item);
                    const canPreview = isServiceMediaPreviewable(item);

                    return (
                        <div key={key} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 md:flex-row md:items-start">
                            <button
                                type="button"
                                onClick={() => handleOpenMedia(item)}
                                disabled={!url}
                                className="h-32 w-full shrink-0 overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-left transition hover:border-[#414141] disabled:cursor-default disabled:hover:border-[#d9d9d9] md:h-24 md:w-36"
                                aria-label={`Open ${title}`}
                            >
                                {url && canPreview && kind === 'video' ? (
                                    <video
                                        src={url}
                                        muted
                                        preload="metadata"
                                        className="h-full w-full bg-black object-cover"
                                        aria-label={title}
                                    />
                                ) : url && canPreview ? (
                                    <img
                                        src={url}
                                        alt={title}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <span className="flex h-full w-full items-center justify-center text-xs font-black text-[#414141]">Media</span>
                                )}
                            </button>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="rounded-full bg-[#f5f5f5] px-2.5 py-1 text-[11px] font-black text-[#414141]">
                                        Intake {label}
                                    </span>
                                    {item.created_at && (
                                        <span className="text-xs font-bold text-[#414141]">
                                            {new Date(item.created_at).toLocaleString()}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 max-w-full truncate text-sm font-black text-[#414141]">{title}</p>
                                {item.caption && <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">{item.caption}</p>}
                            </div>
                            {url && (
                                <button
                                    type="button"
                                    onClick={() => handleOpenMedia(item)}
                                    className="inline-flex min-h-[34px] shrink-0 items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                                >
                                    Open
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            <ServiceMediaPreviewModal item={previewMedia} onClose={() => setPreviewMedia(null)} />
        </>
    );
}

function getCompletedCheckStatusClass(outcome) {
    if (outcome === 'normal') return 'bg-[#eefbf3] text-[#0a8f53]';
    if (outcome === 'issue') return 'bg-[#fff1f1] text-[#b42318]';
    return 'bg-[#fff8e8] text-[#936000]';
}

function CompletedDiagnosticChecks({ checks = [] }) {
    if (!checks.length) {
        return <p className="text-sm font-medium text-[#414141]">No completed diagnostic checks were attached.</p>;
    }

    return (
        <div className="divide-y divide-[#ededed]">
            {checks.map((check, index) => (
                <div key={check.id || `${check.recommendedCheck}-${index}`} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-black text-[#414141]">{check.recommendedCheck}</p>
                        {check.outcomeLabel && (
                            <span className={`rounded-full px-2.5 py-1 text-[11px] font-black ${getCompletedCheckStatusClass(check.outcome)}`}>
                                {check.outcomeLabel}
                            </span>
                        )}
                    </div>
                    {check.summary && (
                        <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">{check.summary}</p>
                    )}
                    {check.notes && (
                        <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">Notes: {check.notes}</p>
                    )}
                </div>
            ))}
        </div>
    );
}

function ShopReadyOverview({ overview }) {
    if (!overview) return null;

    return (
        <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <p className="text-base font-black leading-snug text-[#414141]">{overview.customerComplaint}</p>
                    <p className="mt-1 text-sm font-medium text-[#414141]">{overview.vehicleName}</p>
                </div>
            </div>

            <div className="mt-4 grid gap-4 border-t border-[#d9d9d9] pt-4 sm:grid-cols-2 xl:grid-cols-3">
                <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Owner / Customer</p>
                    <p className="mt-2 text-sm font-black text-[#414141]">{overview.customerName}</p>
                    <p className="mt-1 break-words text-sm font-medium text-[#414141]">
                        {overview.customerPhone} / {overview.customerEmail}
                    </p>
                </div>
                <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Vehicle</p>
                    <p className="mt-2 text-sm font-black text-[#414141]">{overview.vehicleName}</p>
                </div>
                <div className="sm:col-span-2 xl:col-span-1">
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Customer Complaint</p>
                    <p className="mt-2 text-sm font-medium leading-relaxed text-[#414141]">{overview.customerComplaint}</p>
                </div>
            </div>

            <div className="mt-4 grid gap-4 border-t border-[#d9d9d9] pt-4 lg:grid-cols-3">
                <div>
                    <p className="mb-2 text-xs font-black text-[#414141]">Observed Symptoms</p>
                    <ListItems items={overview.symptoms} emptyText="No observed symptoms captured." />
                </div>
                <div>
                    <p className="mb-2 text-xs font-black text-[#414141]">Likely Causes</p>
                    <ListItems items={overview.likelyCauses} emptyText="Pending shop review." />
                </div>
                <div>
                    <p className="mb-2 text-xs font-black text-[#414141]">Recommended Checks</p>
                    <ListItems items={overview.recommendedChecks} emptyText="Inspect and confirm root cause." />
                </div>
            </div>

            <div className="mt-4 border-t border-[#d9d9d9] pt-4">
                <p className="mb-2 text-xs font-black text-[#414141]">Completed Diagnostic Checks</p>
                <CompletedDiagnosticChecks checks={overview.completedChecks} />
            </div>
        </div>
    );
}

// Seed the builder from an already-sent estimate so the shop edits/updates the EXISTING items
// instead of starting from a blank template. Falls back to the default starter lines.
function estimateLinesFromRequest(request) {
    const existing = request?.latest_estimate?.lines;
    if (Array.isArray(existing) && existing.length > 0) {
        return existing.map((line) => ({
            line_type: line.line_type || 'other',
            description: line.description || '',
            amount: line.amount != null ? String(line.amount) : '',
            approval_group_label: line.approval_group_label || '',
        }));
    }
    return DEFAULT_ESTIMATE_LINES.map((line) => ({ ...line }));
}

function getEstimateGroupLabel(line) {
    return String(line?.approval_group_label || '').trim();
}

function getEstimateGroupSectionKey(line) {
    const uiKey = String(line?._approval_group_ui_key || '').trim();
    if (uiKey) return uiKey;
    const label = getEstimateGroupLabel(line);
    return label ? `label:${label.toLowerCase()}` : '';
}

function getEstimateLineSections(lines = []) {
    const groups = [];
    const groupsByKey = new Map();
    const ungrouped = [];

    lines.forEach((line, index) => {
        const key = getEstimateGroupSectionKey(line);
        const item = { line, index };
        if (!key) {
            ungrouped.push(item);
            return;
        }
        if (!groupsByKey.has(key)) {
            const group = { key, label: getEstimateGroupLabel(line), items: [] };
            groupsByKey.set(key, group);
            groups.push(group);
        }
        groupsByKey.get(key).items.push(item);
    });

    return { ungrouped, groups };
}

function getNextEstimateGroupLabel(lines = []) {
    const existing = new Set(lines.map(getEstimateGroupLabel).filter(Boolean).map((label) => label.toLowerCase()));
    let index = 1;
    while (existing.has(`group ${index}`)) index += 1;
    return `Group ${index}`;
}

function EstimateBuilderLineRow({ line, onChange, onRemove }) {
    return (
        <div className="grid gap-2 rounded-lg border border-[#d9d9d9] bg-white p-2 sm:grid-cols-[112px_minmax(0,1fr)_96px_32px]">
            <select
                value={line.line_type}
                onChange={(event) => onChange({ line_type: event.target.value })}
                className="h-9 rounded-lg border border-[#d9d9d9] bg-white px-2 text-xs font-bold text-[#414141] outline-none focus:border-[#D81F26]"
            >
                <option value="diagnostic">Diagnostic</option>
                <option value="parts">Parts</option>
                <option value="labor">Labor</option>
                <option value="tax">Tax</option>
                <option value="other">Other</option>
            </select>
            <input
                value={line.description}
                onChange={(event) => onChange({ description: event.target.value })}
                placeholder={getEstimateLineDescriptionPlaceholder(line.line_type)}
                className="h-9 min-w-0 rounded-lg border border-[#d9d9d9] bg-white px-2.5 text-sm font-medium text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
            />
            <input
                type="number"
                min="0"
                value={line.amount}
                onChange={(event) => onChange({ amount: event.target.value })}
                placeholder="Amount"
                className="h-9 rounded-lg border border-[#d9d9d9] bg-white px-2.5 text-sm font-medium text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
            />
            <button
                type="button"
                onClick={onRemove}
                className="flex h-9 items-center justify-center rounded-lg text-[#b42318] hover:bg-[#fff1f1]"
                aria-label="Remove estimate line"
            >
                <Icon className="h-4 w-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>
            </button>
        </div>
    );
}

function normalizeServiceNotesForm(notes = {}) {
    const partRows = Array.isArray(notes.parts_fluids) ? notes.parts_fluids : [];
    const inspectionRows = Array.isArray(notes.inspection_results) ? notes.inspection_results : [];
    return {
        work_performed: notes.work_performed || '',
        confirmed_diagnosis: notes.confirmed_diagnosis || '',
        root_cause: notes.root_cause || '',
        future_recommendations: notes.future_recommendations || '',
        monitoring_items: notes.monitoring_items || '',
        estimate_deviations: notes.estimate_deviations || '',
        owner_comments: notes.owner_comments || '',
        completion_mileage: normalizeWholeNumberInput(notes.completion_mileage),
        completed_at: toDatetimeLocalValue(notes.completed_at),
        parts_fluids: partRows.length
            ? partRows.map((item) => ({
                ...DEFAULT_SERVICE_PART_FLUID,
                ...item,
                quantity: normalizeDecimalNumberInput(item?.quantity),
            }))
            : [{ ...DEFAULT_SERVICE_PART_FLUID }],
        inspection_results: inspectionRows.length ? inspectionRows.map((item) => ({ ...DEFAULT_SERVICE_INSPECTION, ...item })) : [{ ...DEFAULT_SERVICE_INSPECTION }],
    };
}

function cleanServiceRows(rows = [], keys = [], contentKeys = keys) {
    return rows
        .map((row) => Object.fromEntries(
            keys.map((key) => [key, String(row?.[key] || '').trim()])
        ))
        .filter((row) => contentKeys.some((key) => row[key]));
}

function normalizeWholeNumberInput(value) {
    return String(value ?? '').replace(/[^\d]/g, '');
}

function normalizeDecimalNumberInput(value) {
    const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
    const [whole = '', ...decimalParts] = cleaned.split('.');
    return decimalParts.length ? `${whole}.${decimalParts.join('')}` : whole;
}

function serviceNotesPayloadFromForm(form) {
    const mileageText = normalizeWholeNumberInput(form.completion_mileage);
    const completedAt = form.completed_at ? new Date(form.completed_at) : null;
    return {
        work_performed: form.work_performed.trim() || null,
        confirmed_diagnosis: form.confirmed_diagnosis.trim() || null,
        root_cause: form.root_cause.trim() || null,
        future_recommendations: form.future_recommendations.trim() || null,
        monitoring_items: form.monitoring_items.trim() || null,
        estimate_deviations: form.estimate_deviations.trim() || null,
        owner_comments: form.owner_comments.trim() || null,
        completion_mileage: mileageText ? Number(mileageText) : null,
        completed_at: completedAt && Number.isFinite(completedAt.getTime()) ? completedAt.toISOString() : null,
        parts_fluids: cleanServiceRows(
            form.parts_fluids,
            ['item_type', 'name', 'brand', 'part_number', 'quantity', 'unit', 'notes'],
            ['name', 'brand', 'part_number', 'quantity', 'unit', 'notes']
        ),
        inspection_results: cleanServiceRows(
            form.inspection_results,
            ['area', 'result', 'measurement', 'status', 'notes']
        ),
    };
}

function toDatetimeLocalValue(value) {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
}

function getDatePartFromDatetimeLocal(value) {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value || ''));
    return match ? match[1] : '';
}

function getTimePartFromDatetimeLocal(value) {
    const match = /T(\d{2}:\d{2})/.exec(String(value || ''));
    return match ? match[1] : '';
}

function buildDatetimeLocalValue(datePart, timePart = '') {
    return datePart ? `${datePart}T${timePart}` : '';
}

function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = () => reject(reader.error || new Error('Could not read media file.'));
        reader.readAsDataURL(file);
    });
}

function getUploadFileExtension(name = '') {
    const match = String(name || '').toLowerCase().match(/\.([^.]+)$/);
    return match ? match[1] : '';
}

function getServiceMediaValidationError(file = {}) {
    const filename = file.name || 'This file';
    const extension = getUploadFileExtension(filename);
    const mimeType = String(file.type || '').toLowerCase();
    if (file.size > MAX_SERVICE_MEDIA_BYTES) {
        return `${filename} is larger than 50 MB.`;
    }
    if (
        mimeType.startsWith('image/')
        || mimeType.startsWith('video/')
        || SERVICE_MEDIA_ALLOWED_EXTENSIONS.has(extension)
    ) {
        return '';
    }
    return `${filename} file type is not supported. ${SERVICE_MEDIA_SUPPORTED_FILE_TYPES_MESSAGE}`;
}

function serviceNoteSyncLabel(serviceNotes = {}) {
    if (serviceNotes?.glovebox_sync?.service_event_id || serviceNotes?.synced_service_event_id) {
        return 'Synced to Glove Box';
    }
    return 'Not synced to Glove Box';
}

function EstimateBuilder({ request, onSent, isEditing = false, onCancelEdit }) {
    const [message, setMessage] = useState(() => request?.latest_estimate?.message || '');
    const [lines, setLines] = useState(() => estimateLinesFromRequest(request));
    const [isSending, setIsSending] = useState(false);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setMessage(request?.latest_estimate?.message || '');
        setLines(estimateLinesFromRequest(request));
        setError('');
    }, [request?.request_id, request?.latest_estimate?.estimate_id]);

    const total = lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const isUpdate = Boolean(request?.latest_estimate) && isEditing;
    const lineSections = getEstimateLineSections(lines);

    function applyPreset(preset) {
        setMessage(preset.message);
        setLines(preset.lines.map((line) => ({ ...line })));
        setError('');
    }

    async function handleSuggest() {
        setIsSuggesting(true);
        setError('');
        try {
            const suggestion = await suggestShopDashboardEstimate(request.request_id);
            if (!suggestion) {
                setError('Could not suggest line items right now. Use a preset or add lines manually.');
                return;
            }
            setLines(suggestion.lines.map((line) => ({
                line_type: line.line_type || 'other',
                description: line.description || '',
                amount: line.amount != null ? String(line.amount) : '',
                approval_group_label: line.approval_group_label || '',
            })));
            // Only fill the note when the shop hasn't written one, so a suggestion never
            // clobbers what the advisor already typed.
            setMessage((prev) => (prev.trim() ? prev : (suggestion.note || '')));
        } catch (err) {
            setError(err?.message || 'Could not suggest line items.');
        } finally {
            setIsSuggesting(false);
        }
    }

    function updateLine(index, patch) {
        setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
    }

    function removeLine(index) {
        setLines((prev) => prev.filter((_, i) => i !== index));
    }

    function addStandaloneLine() {
        setLines((prev) => [...prev, { ...EMPTY_ESTIMATE_LINE }]);
    }

    function addGroup() {
        setLines((prev) => {
            const label = getNextEstimateGroupLabel(prev);
            const groupKey = `new:${Date.now()}:${prev.length}`;
            return [...prev, { ...EMPTY_ESTIMATE_LINE, _approval_group_ui_key: groupKey, approval_group_label: label }];
        });
    }

    function addLineToGroup(group) {
        setLines((prev) => [...prev, {
            ...EMPTY_ESTIMATE_LINE,
            _approval_group_ui_key: group.key,
            approval_group_label: group.label,
        }]);
    }

    function renameGroup(groupKey, nextLabel) {
        setLines((prev) => prev.map((line) => (
            getEstimateGroupSectionKey(line) === groupKey
                ? { ...line, _approval_group_ui_key: groupKey, approval_group_label: nextLabel }
                : line
        )));
    }

    async function handleSend() {
        if (lines.some((line) => getEstimateGroupSectionKey(line) && !getEstimateGroupLabel(line))) {
            setError('Name each estimate group before sending.');
            return;
        }

        const cleanedLines = lines
            .map((line) => {
                const rawAmount = String(line.amount ?? '').trim();

                return {
                    description: getEstimateLineSubmissionDescription(line),
                    amount: rawAmount === '' ? NaN : Number(rawAmount),
                    line_type: line.line_type || 'other',
                    approval_group_label: String(line.approval_group_label || '').trim() || null,
                };
            })
            .filter((line) => Number.isFinite(line.amount) && line.amount >= 0);

        if (!cleanedLines.length) {
            setError('Add at least one estimate amount.');
            return;
        }

        setIsSending(true);
        setError('');
        try {
            const updated = await sendShopDashboardEstimate(request.request_id, {
                message,
                currency: 'USD',
                lines: cleanedLines,
            });
            onSent(updated);
        } catch (err) {
            setError(err?.message || 'Could not send estimate.');
        } finally {
            setIsSending(false);
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={handleSuggest}
                    disabled={isSuggesting}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-[#d8d8d8] bg-[#f5f5f5] px-3 py-1.5 text-xs font-black text-[#414141] hover:bg-[#eeeeee] disabled:cursor-wait disabled:opacity-60"
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.6 4.8L18 9.4l-4.4 1.6L12 16l-1.6-5L6 9.4l4.4-1.6z" /><path d="M5 19h6" /></svg>
                    {isSuggesting ? 'Suggesting…' : 'Suggest line items'}
                </button>
                <span className="text-[11px] font-bold text-[#9e9e9e]">or start from a template:</span>
                {ESTIMATE_PRESETS.map((preset) => (
                    <button
                        key={preset.label}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-1.5 text-xs font-black text-[#414141] hover:bg-[#F2F2F2]"
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            {isUpdate && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
                    You're updating a sent estimate. The driver will be asked to review and approve the changes — add a note below explaining what changed.
                </p>
            )}

            <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={ESTIMATE_MESSAGE_PLACEHOLDER}
                className="min-h-[82px] w-full resize-y rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2 text-sm font-medium text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
            />

            <div className="space-y-2">
                {lineSections.ungrouped.length > 0 && (
                    <div className="rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] p-2">
                        <div className="mb-2 flex items-center justify-between gap-2 px-1">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Individual line items</p>
                            <span className="text-[11px] font-bold text-[#9e9e9e]">Approved one at a time</span>
                        </div>
                        <div className="space-y-2">
                            {lineSections.ungrouped.map(({ line, index }) => (
                                <EstimateBuilderLineRow
                                    key={index}
                                    line={line}
                                    onChange={(patch) => updateLine(index, patch)}
                                    onRemove={() => removeLine(index)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {lineSections.groups.map((group) => (
                    <div key={group.key} className="rounded-xl border border-[#d8d8d8] bg-[#f5f5f5] p-2">
                        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <label className="min-w-0 flex-1">
                                <span className="sr-only">Approval group name</span>
                                <input
                                    value={group.label}
                                    onChange={(event) => renameGroup(group.key, event.target.value)}
                                    placeholder="Group name"
                                    aria-label="Approval group name"
                                    className="h-9 w-full rounded-lg border border-[#d8d8d8] bg-white px-2.5 text-sm font-black text-[#414141] outline-none placeholder:text-[#9e9e9e] focus:border-[#D81F26]"
                                />
                            </label>
                            <div className="flex items-center gap-2">
                                <span className="rounded-full border border-[#d8d8d8] bg-white px-2 py-1 text-[10px] font-black text-[#6B6B6B]">
                                    Grouped approval
                                </span>
                                <button
                                    type="button"
                                    onClick={() => addLineToGroup(group)}
                                    className="rounded-lg border border-[#d8d8d8] bg-white px-2.5 py-1.5 text-xs font-black text-[#414141] hover:bg-[#F2F2F2]"
                                >
                                    Add item
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {group.items.map(({ line, index }) => (
                                <EstimateBuilderLineRow
                                    key={index}
                                    line={line}
                                    onChange={(patch) => updateLine(index, patch)}
                                    onRemove={() => removeLine(index)}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={addStandaloneLine}
                        className="rounded-xl border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-black text-[#414141] hover:bg-[#F2F2F2]"
                    >
                        Add line
                    </button>
                    <button
                        type="button"
                        onClick={addGroup}
                        className="rounded-xl border border-[#d8d8d8] bg-[#f5f5f5] px-3 py-2 text-xs font-black text-[#414141] hover:bg-[#eeeeee]"
                    >
                        Add group
                    </button>
                </div>
                <p className="text-sm font-black text-[#414141]">Total {formatPartnerMoney(total)}</p>
            </div>

            {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}

            <div className="flex justify-center">
                {isEditing && onCancelEdit && (
                    <button
                        type="button"
                        onClick={onCancelEdit}
                        disabled={isSending}
                        className="mr-2 inline-flex h-10 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] hover:bg-[#F9F9F9] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleSend}
                    disabled={isSending}
                    className="inline-flex h-10 items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isSending ? 'Sending estimate' : isUpdate ? 'Resend estimate' : 'Send estimate'}
                </button>
            </div>
        </div>
    );
}

function ServiceNotesPanel({ request, onUpdated }) {
    const requestId = request?.request_id || '';
    const [form, setForm] = useState(() => normalizeServiceNotesForm(request?.service_notes));
    const [saveState, setSaveState] = useState('saved');
    const [error, setError] = useState('');
    const [mediaPhase, setMediaPhase] = useState('before');
    const [isUploading, setIsUploading] = useState(false);
    const [previewMedia, setPreviewMedia] = useState(null);
    const savedSignatureRef = useRef('');

    useEffect(() => {
        const nextForm = normalizeServiceNotesForm(request?.service_notes);
        setForm(nextForm);
        savedSignatureRef.current = JSON.stringify(serviceNotesPayloadFromForm(nextForm));
        setSaveState('saved');
        setError('');
        setPreviewMedia(null);
    }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

    const payload = useMemo(() => serviceNotesPayloadFromForm(form), [form]);
    const payloadSignature = useMemo(() => JSON.stringify(payload), [payload]);
    const media = Array.isArray(request?.service_notes?.media) ? request.service_notes.media : [];
    const syncLabel = serviceNoteSyncLabel(request?.service_notes);
    const hasSynced = syncLabel === 'Synced to Glove Box';
    const completionDateValue = getDatePartFromDatetimeLocal(form.completed_at);
    const completionTimeValue = getTimePartFromDatetimeLocal(form.completed_at);

    useEffect(() => {
        if (!requestId || payloadSignature === savedSignatureRef.current) return undefined;
        setSaveState('not_saved');
        const timer = setTimeout(async () => {
            setSaveState('saving');
            setError('');
            try {
                const updated = await saveShopDashboardServiceNotes(requestId, payload);
                savedSignatureRef.current = payloadSignature;
                setSaveState('saved');
                if (updated) onUpdated(updated);
            } catch (err) {
                setSaveState('error');
                setError(err?.message || 'Could not save service notes.');
            }
        }, 800);
        return () => clearTimeout(timer);
    }, [requestId, payloadSignature]); // eslint-disable-line react-hooks/exhaustive-deps

    async function handleSaveNow() {
        if (!requestId) return;
        setSaveState('saving');
        setError('');
        try {
            const currentPayload = serviceNotesPayloadFromForm(form);
            const updated = await saveShopDashboardServiceNotes(requestId, currentPayload);
            savedSignatureRef.current = JSON.stringify(currentPayload);
            setSaveState('saved');
            if (updated) onUpdated(updated, 'Service notes saved.');
        } catch (err) {
            setSaveState('error');
            setError(err?.message || 'Could not save service notes.');
        }
    }

    function updateTextField(key, value) {
        setForm((current) => ({ ...current, [key]: value }));
    }

    function updateCompletionMileage(value) {
        updateTextField('completion_mileage', normalizeWholeNumberInput(value));
    }

    function updateCompletionDate(value) {
        updateTextField('completed_at', buildDatetimeLocalValue(value, completionTimeValue));
    }

    function updateCompletionTime(value) {
        if (!completionDateValue) return;
        updateTextField('completed_at', buildDatetimeLocalValue(completionDateValue, value));
    }

    function updatePartFluid(index, key, value) {
        const nextValue = key === 'quantity' ? normalizeDecimalNumberInput(value) : value;
        setForm((current) => ({
            ...current,
            parts_fluids: current.parts_fluids.map((item, itemIndex) => (
                itemIndex === index ? { ...item, [key]: nextValue } : item
            )),
        }));
    }

    function updateInspection(index, key, value) {
        setForm((current) => ({
            ...current,
            inspection_results: current.inspection_results.map((item, itemIndex) => (
                itemIndex === index ? { ...item, [key]: value } : item
            )),
        }));
    }

    async function handleUploadMedia(event) {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!requestId || !files.length || isUploading) return;
        const rejected = [];
        const validFiles = files.filter((file) => {
            const rejection = getServiceMediaValidationError(file);
            if (rejection) {
                rejected.push(rejection);
                return false;
            }
            return true;
        });
        if (rejected.length > 0) {
            setError(rejected.slice(0, 3).join(' '));
        }
        if (!validFiles.length) return;
        setIsUploading(true);
        if (rejected.length === 0) setError('');
        try {
            let latest = null;
            for (const file of validFiles) {
                const dataBase64 = await readFileAsBase64(file);
                latest = await uploadShopDashboardServiceNoteMedia(requestId, {
                    filename: file.name,
                    mime_type: file.type || 'application/octet-stream',
                    phase: mediaPhase,
                    data_base64: dataBase64,
                });
            }
            if (latest) onUpdated(latest, 'Service media uploaded.');
        } catch (err) {
            setError(err?.message || 'Could not upload service media.');
        } finally {
            setIsUploading(false);
        }
    }

    async function handleRemoveMedia(mediaId) {
        if (!requestId || !mediaId) return;
        setError('');
        try {
            const updated = await deleteShopDashboardServiceNoteMedia(requestId, mediaId);
            if (updated) onUpdated(updated, 'Service media removed.');
        } catch (err) {
            setError(err?.message || 'Could not remove service media.');
        }
    }

    function handleOpenMedia(item) {
        const url = getServiceMediaUrl(item);
        if (!url) return;
        if (isServiceMediaPreviewable(item)) {
            setPreviewMedia(item);
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    const saveLabel = {
        saved: 'Saved',
        saving: 'Saving',
        not_saved: 'Not saved',
        error: 'Save failed',
    }[saveState] || 'Saved';

    return (
        <div className="space-y-4">
            <DetailSection title="Service record" className="shadow-none">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <p className="text-sm font-black text-[#414141]">Technician notes</p>
                        <p className="mt-1 text-sm font-medium text-[#414141]">
                            {request?.request_status === 'completed'
                                ? 'Completed service record'
                                : `${getVehicleName(request)} / ${getTicketLabel(request) || 'Active job'}`}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex min-h-[30px] items-center rounded-full border px-3 text-xs font-black ${
                            saveState === 'error'
                                ? 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'
                                : 'border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]'
                        }`}>
                            {saveLabel}
                        </span>
                        <span className={`inline-flex min-h-[30px] items-center rounded-full border px-3 text-xs font-black ${
                            hasSynced
                                ? 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'
                                : 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]'
                        }`}>
                            {syncLabel}
                        </span>
                        <button
                            type="button"
                            onClick={handleSaveNow}
                            disabled={saveState === 'saving'}
                            className="inline-flex min-h-[30px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2] disabled:cursor-wait disabled:opacity-60"
                        >
                            Save now
                        </button>
                    </div>
                </div>

                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    {SERVICE_NOTE_TEXT_FIELDS.map((field) => (
                        <label key={field.key} className={field.key === 'work_performed' ? 'lg:col-span-2' : ''}>
                            <span className="text-xs font-black text-[#414141]">{field.label}</span>
                            <textarea
                                value={form[field.key]}
                                onChange={(event) => updateTextField(field.key, event.target.value)}
                                rows={field.rows}
                                placeholder={field.placeholder}
                                className="mt-1 w-full resize-y rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:bg-white"
                            />
                        </label>
                    ))}
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label>
                        <span className="text-xs font-black text-[#414141]">Completion mileage</span>
                        <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            step="1"
                            pattern="[0-9]*"
                            value={form.completion_mileage}
                            onChange={(event) => updateCompletionMileage(event.target.value)}
                            placeholder={request?.vehicle?.mileage || 'Mileage'}
                            className="mt-1 min-h-[40px] w-full rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:bg-white"
                        />
                    </label>
                    <div className="max-w-full lg:max-w-[480px]">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                            <label className="block w-full sm:w-[210px]">
                                <span className="text-xs font-black text-[#414141]">Completion date</span>
                                <input
                                    type="date"
                                    value={completionDateValue}
                                    onChange={(event) => updateCompletionDate(event.target.value)}
                                    aria-label="Completion date"
                                    className="mt-1 min-h-[40px] w-full rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:bg-white"
                                />
                            </label>
                            {completionDateValue && (
                                <label className="block w-full sm:w-[190px]">
                                    <span className="text-xs font-black text-[#414141]">Completion time</span>
                                    <div className="mt-1">
                                        <AppointmentClockPicker
                                            id="service-completion-time"
                                            value={completionTimeValue}
                                            onChange={updateCompletionTime}
                                            placeholder="Select time"
                                            align="left"
                                        />
                                    </div>
                                </label>
                            )}
                        </div>
                    </div>
                </div>

                {error && <p className="mt-3 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{error}</p>}
            </DetailSection>

            <DetailSection title="Parts and fluids" className="shadow-none">
                <div className="space-y-3">
                    {form.parts_fluids.map((item, index) => (
                        <div key={index} className="border-t border-[#d9d9d9] pt-3 first:border-t-0 first:pt-0">
                            <div className="grid gap-2 lg:grid-cols-[110px_minmax(150px,1.2fr)_minmax(120px,0.9fr)_minmax(120px,0.9fr)_90px_80px_auto]">
                                <select
                                    aria-label={`Item type ${index + 1}`}
                                    value={item.item_type}
                                    onChange={(event) => updatePartFluid(index, 'item_type', event.target.value)}
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-2 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                >
                                    <option value="part">Part</option>
                                    <option value="fluid">Fluid</option>
                                    <option value="supply">Supply</option>
                                    <option value="other">Other</option>
                                </select>
                                <input
                                    value={item.name}
                                    onChange={(event) => updatePartFluid(index, 'name', event.target.value)}
                                    placeholder="Name"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <input
                                    value={item.brand}
                                    onChange={(event) => updatePartFluid(index, 'brand', event.target.value)}
                                    placeholder="Brand"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <input
                                    value={item.part_number}
                                    onChange={(event) => updatePartFluid(index, 'part_number', event.target.value)}
                                    placeholder="Part number"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    step="any"
                                    value={item.quantity}
                                    onChange={(event) => updatePartFluid(index, 'quantity', event.target.value)}
                                    placeholder="Qty"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <input
                                    value={item.unit}
                                    onChange={(event) => updatePartFluid(index, 'unit', event.target.value)}
                                    placeholder="Unit"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <button
                                    type="button"
                                    onClick={() => setForm((current) => ({
                                        ...current,
                                        parts_fluids: current.parts_fluids.length > 1
                                            ? current.parts_fluids.filter((_, itemIndex) => itemIndex !== index)
                                            : [{ ...DEFAULT_SERVICE_PART_FLUID }],
                                    }))}
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#b42318] transition hover:bg-[#fff1f1]"
                                >
                                    Remove
                                </button>
                            </div>
                            <input
                                value={item.notes}
                                onChange={(event) => updatePartFluid(index, 'notes', event.target.value)}
                                placeholder="Notes"
                                className="mt-2 min-h-[38px] w-full rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                            />
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => setForm((current) => ({
                            ...current,
                            parts_fluids: [...current.parts_fluids, { ...DEFAULT_SERVICE_PART_FLUID }],
                        }))}
                        className="inline-flex min-h-[38px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2]"
                    >
                        Add part or fluid
                    </button>
                </div>
            </DetailSection>

            <DetailSection title="Inspection results" className="shadow-none">
                <div className="space-y-3">
                    {form.inspection_results.map((item, index) => (
                        <div key={index} className="border-t border-[#d9d9d9] pt-3 first:border-t-0 first:pt-0">
                            <div className="grid gap-2 lg:grid-cols-[minmax(130px,1fr)_minmax(160px,1.3fr)_minmax(120px,1fr)_120px_auto]">
                                <input
                                    value={item.area}
                                    onChange={(event) => updateInspection(index, 'area', event.target.value)}
                                    placeholder="System or area"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <input
                                    value={item.result}
                                    onChange={(event) => updateInspection(index, 'result', event.target.value)}
                                    placeholder="Result"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <input
                                    value={item.measurement}
                                    onChange={(event) => updateInspection(index, 'measurement', event.target.value)}
                                    placeholder="Measurement"
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                />
                                <select
                                    aria-label={`Inspection status ${index + 1}`}
                                    value={item.status}
                                    onChange={(event) => updateInspection(index, 'status', event.target.value)}
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-2 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                                >
                                    {SERVICE_INSPECTION_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setForm((current) => ({
                                        ...current,
                                        inspection_results: current.inspection_results.length > 1
                                            ? current.inspection_results.filter((_, itemIndex) => itemIndex !== index)
                                            : [{ ...DEFAULT_SERVICE_INSPECTION }],
                                    }))}
                                    className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#b42318] transition hover:bg-[#fff1f1]"
                                >
                                    Remove
                                </button>
                            </div>
                            <input
                                value={item.notes}
                                onChange={(event) => updateInspection(index, 'notes', event.target.value)}
                                placeholder="Notes"
                                className="mt-2 min-h-[38px] w-full rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                            />
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => setForm((current) => ({
                            ...current,
                            inspection_results: [...current.inspection_results, { ...DEFAULT_SERVICE_INSPECTION }],
                        }))}
                        className="inline-flex min-h-[38px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2]"
                    >
                        Add inspection result
                    </button>
                </div>
            </DetailSection>

            <DetailSection title="Before and after media" className="shadow-none">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            aria-label="Media phase"
                            value={mediaPhase}
                            onChange={(event) => setMediaPhase(event.target.value)}
                            className="min-h-[38px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none focus:border-[#D81F26] focus:bg-white"
                        >
                            {SERVICE_MEDIA_PHASE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <label className="inline-flex min-h-[38px] cursor-pointer items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2]">
                            {isUploading ? 'Uploading' : 'Upload media'}
                            <input
                                type="file"
                                multiple
                                disabled={isUploading}
                                onChange={handleUploadMedia}
                                className="sr-only"
                            />
                        </label>
                    </div>
                    <p className="text-xs font-bold text-[#414141]">{media.length} attached</p>
                </div>

                {media.length > 0 ? (
                    <div className="mt-3 divide-y divide-[#d9d9d9]">
                        {media.map((item) => {
                            const kind = getServiceMediaKind(item);
                            const url = getServiceMediaUrl(item);
                            const title = item.filename || 'Service media';
                            const canOpen = Boolean(url);
                            const canPreview = isServiceMediaPreviewable(item);

                            return (
                                <div key={item.media_id} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex min-w-0 flex-1 gap-3">
                                        {canOpen ? (
                                            <button
                                                type="button"
                                                onClick={() => handleOpenMedia(item)}
                                                className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] transition hover:border-[#414141]"
                                                aria-label={`Open ${title}`}
                                            >
                                                {canPreview && kind === 'video' ? (
                                                    <video src={url} muted preload="metadata" className="h-full w-full bg-black object-cover" aria-label={title} />
                                                ) : canPreview && kind === 'photo' ? (
                                                    <img src={url} alt="" className="h-full w-full object-cover" />
                                                ) : (
                                                    <span className="flex h-full w-full items-center justify-center text-xs font-black text-[#414141]">File</span>
                                                )}
                                            </button>
                                        ) : (
                                            <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] text-xs font-black text-[#414141]">
                                                File
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="rounded-full bg-[#f5f5f5] px-2.5 py-1 text-[11px] font-black text-[#414141]">
                                                    {SERVICE_MEDIA_PHASE_OPTIONS.find((option) => option.value === item.phase)?.label || 'During'}
                                                </span>
                                                <span className="rounded-full bg-[#F9F9F9] px-2.5 py-1 text-[11px] font-black text-[#414141]">
                                                    {kind === 'video' ? 'Video' : kind === 'photo' ? 'Photo' : 'File'}
                                                </span>
                                            </div>
                                            <p className="mt-1 max-w-full truncate text-sm font-black text-[#414141]">{title}</p>
                                            {item.caption && <p className="mt-1 text-sm font-medium text-[#414141]">{item.caption}</p>}
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 gap-2">
                                        {canOpen && (
                                            <button
                                                type="button"
                                                onClick={() => handleOpenMedia(item)}
                                                className="inline-flex min-h-[32px] items-center rounded-full border border-[#d9d9d9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                                            >
                                                Open
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveMedia(item.media_id)}
                                            className="inline-flex min-h-[32px] items-center rounded-full border border-[#ffd2d2] bg-white px-3 text-xs font-black text-[#b42318] transition hover:bg-[#fff1f1]"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="mt-3 text-sm font-medium text-[#414141]">No service media attached yet.</p>
                )}
            </DetailSection>
            <ServiceMediaPreviewModal item={previewMedia} onClose={() => setPreviewMedia(null)} />
        </div>
    );
}

const todayISODate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatReferralTime = (hhmm = '') => {
    if (!String(hhmm || '').trim()) return '';
    const [rawHour, rawMinute] = String(hhmm || '').split(':');
    const hour = Number(rawHour);
    const minute = Number(rawMinute);
    if (!Number.isFinite(hour)) return '';
    const period = hour >= 12 ? 'PM' : 'AM';
    return `${hour % 12 || 12}:${String(Number.isFinite(minute) ? minute : 0).padStart(2, '0')} ${period}`;
};

const formatReferralAppointment = (dateValue = '', timeValue = '') => {
    if (!dateValue) return '';
    const [year, month, day] = String(dateValue).split('-').map(Number);
    if (!year || !month || !day) return '';
    const date = new Date(year, month - 1, day, 12, 0, 0);
    const dateLabel = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const timeLabel = formatReferralTime(timeValue);
    return timeLabel ? `${dateLabel} at ${timeLabel}` : dateLabel;
};

const formatReferralDate = (dateValue = '') => formatReferralAppointment(dateValue, '');

const digitsOnly = (value = '') => String(value || '').replace(/\D/g, '');
const hasUsableReferralPhone = (value = '') => digitsOnly(value).length >= 8;
const REFERRAL_VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;
const sanitizeReferralVin = (value = '') => String(value || '')
    .toUpperCase()
    .replace(/[^A-HJ-NPR-Z0-9]/g, '')
    .slice(0, 17);
const sanitizeNumericField = (value = '', maxLength = 12) => digitsOnly(value).slice(0, maxLength);
const getReferralVehicleFieldState = (form = {}) => {
    const vehicleVin = sanitizeReferralVin(form.vehicle_vin);
    const vehicleYearRaw = sanitizeNumericField(form.vehicle_year, 4);
    const vehicleMileageRaw = sanitizeNumericField(form.vehicle_mileage, 7);
    const vehicleMake = String(form.vehicle_make || '').trim();
    const vehicleModel = String(form.vehicle_model || '').trim();
    const vehicleTrim = String(form.vehicle_trim || '').trim();
    const vehicleEngine = String(form.vehicle_engine || '').trim();
    const vehicleTransmission = String(form.vehicle_transmission || '').trim();
    const vehicleDrivetrain = String(form.vehicle_drivetrain || '').trim();
    const licensePlate = String(form.license_plate || '').trim();
    const hasAnyVehicleField = [
        vehicleVin,
        vehicleYearRaw,
        vehicleMake,
        vehicleModel,
        vehicleTrim,
        vehicleEngine,
        vehicleTransmission,
        vehicleDrivetrain,
        vehicleMileageRaw,
        licensePlate,
    ].some(Boolean);
    return {
        vehicleVin,
        vehicleYearRaw,
        vehicleMileageRaw,
        vehicleMake,
        vehicleModel,
        vehicleTrim,
        vehicleEngine,
        vehicleTransmission,
        vehicleDrivetrain,
        licensePlate,
        hasAnyVehicleField,
    };
};
const REFERRAL_INVITE_INITIAL_FORM = {
    customer_name: '',
    customer_phone_digits: '',
    customer_email: '',
    issue_title: '',
    preliminary_issue: '',
    appointment_date: '',
    appointment_time: '09:00',
    vehicle_vin: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_trim: '',
    vehicle_engine: '',
    vehicle_transmission: '',
    vehicle_drivetrain: '',
    vehicle_mileage: '',
    license_plate: '',
};

function getReferralVehicleName(referral) {
    const vehicle = referral?.vehicle || referral?.vehicle_snapshot || {};
    const label = String(vehicle.vehicleName || vehicle.vehicle_name || '').trim();
    if (label) return label;
    return [
        vehicle.year || referral?.vehicle_year,
        vehicle.make || referral?.vehicle_make,
        vehicle.model || referral?.vehicle_model,
        vehicle.trim || referral?.vehicle_trim,
    ].filter(Boolean).join(' ').trim();
}

function buildExistingReferralCustomers(requests = [], referrals = []) {
    const byKey = new Map();
    const addCustomer = ({ name, phone, email, vehicle, lastIssue, updatedAt }) => {
        if (!phone && !email && (!name || name === 'Customer')) return;
        const key = `${name.toLowerCase()}|${digitsOnly(phone)}|${email.toLowerCase()}`;
        const next = {
            key,
            name,
            phone,
            email,
            vehicle,
            lastIssue,
            updatedAt: updatedAt || '',
        };
        const existing = byKey.get(key);
        if (!existing || String(next.updatedAt).localeCompare(String(existing.updatedAt || '')) > 0) {
            byKey.set(key, next);
        }
    };
    (requests || []).forEach((request) => {
        const customer = request?.customer || {};
        const report = request?.report_snapshot || {};
        addCustomer({
            name: getCustomerName(request),
            phone: String(customer.phone_number || report.customerPhone || report.customer_phone || '').trim(),
            email: String(customer.email || report.customerEmail || report.customer_email || '').trim(),
            vehicle: getVehicleName(request),
            lastIssue: getRequestTitle(request),
            updatedAt: request.updated_at || request.created_at || '',
        });
    });
    (referrals || []).forEach((referral) => {
        addCustomer({
            name: getReferralCustomerName(referral),
            phone: String(referral?.customer_phone || '').trim(),
            email: String(referral?.customer_email || '').trim(),
            vehicle: getReferralVehicleName(referral),
            lastIssue: getReferralIssueTitle(referral),
            updatedAt: referral?.updated_at || referral?.created_at || '',
        });
    });
    return Array.from(byKey.values()).sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
}

const REFERRAL_DELIVERY_CHANNELS = [
    { key: 'sms', label: 'SMS' },
    { key: 'email', label: 'Email' },
];

function getReferralDeliveryLine(result = {}) {
    if (result.status === 'sent') return `Sent${result.to ? ` to ${result.to}` : ''}`;
    if (result.status === 'failed') return `Failed${result.reason ? `: ${result.reason}` : ''}`;
    return `Not sent${result.reason ? `: ${result.reason}` : ''}`;
}

// One small status pill for the automatic SMS/email send of the referral link.
// Green when at least one channel went out, red when none did; hover, focus or tap
// shows each channel's result.
function ReferralLinkDeliveryStatus({ delivery }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const tooltipId = useId();

    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (event) => {
            if (!wrapperRef.current?.contains(event.target)) setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    if (!delivery || typeof delivery !== 'object') return null;
    const channels = REFERRAL_DELIVERY_CHANNELS.filter((channel) => delivery[channel.key]);
    if (!channels.length) return null;
    const anySent = channels.some((channel) => delivery[channel.key].status === 'sent');

    return (
        <span
            ref={wrapperRef}
            className="relative inline-flex"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                data-testid="referral-delivery-status"
                data-status={anySent ? 'sent' : 'failed'}
                aria-describedby={tooltipId}
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                className={`inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-xs font-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D81F26]/40 ${anySent ? 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]' : 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]'}`}
            >
                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${anySent ? 'bg-[#12a150]' : 'bg-[#d92d20]'}`} />
                {anySent ? 'Sent' : 'Not sent'}
            </button>
            <span
                id={tooltipId}
                role="tooltip"
                className={`${open ? 'block' : 'sr-only'} absolute left-0 top-full z-[130] mt-2 w-max max-w-[260px] rounded-xl bg-[#414141] px-3 py-2 text-xs font-semibold leading-relaxed text-white shadow-[0_12px_30px_rgba(20,20,20,0.25)]`}
            >
                {channels.map((channel) => (
                    <span key={channel.key} className="block" data-testid={`referral-delivery-${channel.key}`} data-status={delivery[channel.key].status}>
                        <span className="font-black">{channel.label}:</span> {getReferralDeliveryLine(delivery[channel.key])}
                    </span>
                ))}
            </span>
        </span>
    );
}

function ShopReferralInviteModal({ open, onClose, requests = [], referrals = [], selectedPartnerShopId = '', requiresShopSelection = false }) {
    const dateInputRef = useRef(null);
    const [form, setForm] = useState(REFERRAL_INVITE_INITIAL_FORM);
    const [customerMode, setCustomerMode] = useState('new');
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomerKey, setSelectedCustomerKey] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isLookingUpVin, setIsLookingUpVin] = useState(false);
    const [error, setError] = useState('');
    const [vinLookupError, setVinLookupError] = useState('');
    // Fields auto-filled from a successful VIN decode are locked (read-only) so shop staff
    // can't edit VIN-authoritative details; editing/clearing the VIN unlocks them (mirrors chat).
    const [vinLockedFields, setVinLockedFields] = useState(() => new Set());
    // "Confirm vehicle" validation state: lets staff validate the vehicle (VIN decode, or
    // make/model check) before creating the job, with clear confirmed/failed feedback.
    const [isConfirmingVehicle, setIsConfirmingVehicle] = useState(false);
    const [vehicleConfirmed, setVehicleConfirmed] = useState(false);
    const [vehicleConfirmError, setVehicleConfirmError] = useState('');
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);
    const [generalLink, setGeneralLink] = useState('');
    const [generalCopied, setGeneralCopied] = useState(false);
    const [isLoadingGeneralLink, setIsLoadingGeneralLink] = useState(false);

    const existingCustomers = useMemo(() => buildExistingReferralCustomers(requests, referrals), [requests, referrals]);
    const filteredCustomers = useMemo(() => {
        const query = customerSearch.trim().toLowerCase();
        if (!query) return existingCustomers;
        return existingCustomers.filter((customer) => (
            customer.name.toLowerCase().includes(query) ||
            String(customer.phone || '').toLowerCase().includes(query) ||
            String(customer.email || '').toLowerCase().includes(query) ||
            String(customer.vehicle || '').toLowerCase().includes(query)
        ));
    }, [customerSearch, existingCustomers]);

    useEffect(() => {
        setError('');
        setVinLookupError('');
        setCopied(false);
        setGeneralCopied(false);
        setGeneralLink('');
        setCreated(null);
        if (open) {
            setCustomerMode('new');
            setCustomerSearch('');
            setSelectedCustomerKey('');
            setForm({ ...REFERRAL_INVITE_INITIAL_FORM });
        }
    }, [open]);

    if (!open) return null;

    // Editing any vehicle field invalidates a prior "Confirm vehicle" result.
    const resetVehicleConfirmation = (key) => {
        if (key && (key.startsWith('vehicle_') || key === 'license_plate')) {
            setVehicleConfirmed(false);
            setVehicleConfirmError('');
        }
    };

    const setField = (key) => (event) => {
        setForm((prev) => ({ ...prev, [key]: event.target.value }));
        setError('');
        resetVehicleConfirmation(key);
    };

    const setSanitizedField = (key, sanitizer) => (event) => {
        setForm((prev) => ({ ...prev, [key]: sanitizer(event.target.value) }));
        setError('');
        resetVehicleConfirmation(key);
        if (key === 'vehicle_vin') {
            setVinLookupError('');
            // Editing the VIN unlocks the decoded fields for manual entry.
            setVinLockedFields((prev) => (prev.size ? new Set() : prev));
        }
    };

    const setDirectField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setError('');
    };

    const resetForm = () => {
        setCustomerMode('new');
        setCustomerSearch('');
        setSelectedCustomerKey('');
        setForm({ ...REFERRAL_INVITE_INITIAL_FORM });
        setError('');
        setVinLookupError('');
        setCopied(false);
        setGeneralCopied(false);
        setGeneralLink('');
    };

    const selectExistingCustomer = (customer) => {
        setSelectedCustomerKey(customer.key);
        setForm((prev) => ({
            ...prev,
            customer_name: customer.name || '',
            customer_phone_digits: digitsOnly(customer.phone),
            customer_email: customer.email || '',
        }));
        setError('');
    };

    const appointmentDateLabel = formatReferralDate(form.appointment_date);
    const appointmentTimeLabel = form.appointment_date ? formatReferralTime(form.appointment_time) : '';
    const appointmentLabel = formatReferralAppointment(form.appointment_date, form.appointment_time);
    const customerPhoneDigits = digitsOnly(form.customer_phone_digits);
    const customerPhone = hasUsableReferralPhone(customerPhoneDigits) ? `+${customerPhoneDigits}` : '';

    const openDatePicker = () => {
        const input = dateInputRef.current;
        if (!input) return;
        try { input.showPicker?.(); } catch { /* browser fallback */ }
    };

    async function handleVinLookup() {
        const vin = sanitizeReferralVin(form.vehicle_vin);
        if (!vin) {
            setVinLookupError('Enter a VIN first.');
            return;
        }
        if (!REFERRAL_VIN_PATTERN.test(vin)) {
            setVinLookupError('A VIN is 17 characters, no I, O, or Q.');
            return;
        }
        setIsLookingUpVin(true);
        setVinLookupError('');
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/decode_vin/${encodeURIComponent(vin)}`);
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data?.status !== 'success' || !data?.vehicle) {
                setVinLookupError(data?.error || 'Could not decode this VIN.');
                return;
            }
            const vehicle = data.vehicle;
            const locked = new Set();
            const withLock = (field, value, prevValue) => {
                if (value) { locked.add(field); return value; }
                return prevValue;
            };
            setForm((prev) => ({
                ...prev,
                vehicle_vin: vin,
                vehicle_year: withLock('vehicle_year', vehicle.year, prev.vehicle_year),
                vehicle_make: withLock('vehicle_make', vehicle.make, prev.vehicle_make),
                vehicle_model: withLock('vehicle_model', vehicle.model, prev.vehicle_model),
                vehicle_engine: withLock('vehicle_engine', vehicle.engine, prev.vehicle_engine),
                vehicle_transmission: withLock('vehicle_transmission', vehicle.transmission, prev.vehicle_transmission),
                vehicle_drivetrain: withLock('vehicle_drivetrain', vehicle.drivetrain, prev.vehicle_drivetrain),
            }));
            setVinLockedFields(locked);
        } catch {
            setVinLookupError('VIN lookup failed.');
        } finally {
            setIsLookingUpVin(false);
        }
    }

    // Explicit "Confirm vehicle" validation: decodes the VIN (authoritative + locks fields)
    // when a VIN is present, otherwise validates the make/model combination — with clear
    // confirmed/failed feedback, mirroring the chat Add Vehicle validation.
    async function handleConfirmVehicle() {
        const { vehicleVin, vehicleMake, vehicleModel, vehicleYearRaw, hasAnyVehicleField } =
            getReferralVehicleFieldState(form);
        setVehicleConfirmError('');
        setVinLookupError('');
        if (!hasAnyVehicleField) {
            setVehicleConfirmError('Add a VIN, or make and model, to confirm the vehicle.');
            return;
        }
        if (vehicleVin && !REFERRAL_VIN_PATTERN.test(vehicleVin)) {
            setVehicleConfirmError('Enter a valid 17-character VIN (letters and numbers, no I, O, or Q), or clear the VIN field.');
            return;
        }
        setIsConfirmingVehicle(true);
        try {
            if (vehicleVin) {
                const resp = await fetch(`${getUserDetailsBaseUrl()}/api/decode_vin/${encodeURIComponent(vehicleVin)}`);
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok || data?.status !== 'success' || !data?.vehicle) {
                    setVehicleConfirmError(data?.error || "This VIN doesn't appear to be valid. Please check it, or clear the VIN and enter the details manually.");
                    return;
                }
                // Decode succeeded — fill + lock the decoded fields (authoritative).
                const v = data.vehicle;
                const locked = new Set();
                const withLock = (field, value, prevValue) => {
                    if (value) { locked.add(field); return value; }
                    return prevValue;
                };
                setForm((prev) => ({
                    ...prev,
                    vehicle_vin: vehicleVin,
                    vehicle_year: withLock('vehicle_year', v.year, prev.vehicle_year),
                    vehicle_make: withLock('vehicle_make', v.make, prev.vehicle_make),
                    vehicle_model: withLock('vehicle_model', v.model, prev.vehicle_model),
                    vehicle_engine: withLock('vehicle_engine', v.engine, prev.vehicle_engine),
                    vehicle_transmission: withLock('vehicle_transmission', v.transmission, prev.vehicle_transmission),
                    vehicle_drivetrain: withLock('vehicle_drivetrain', v.drivetrain, prev.vehicle_drivetrain),
                }));
                setVinLockedFields(locked);
            } else {
                // No VIN — validate the make/model (year optional) via the same endpoint chat uses.
                if (!vehicleMake || !vehicleModel) {
                    setVehicleConfirmError('Enter both make and model (or a VIN) to confirm the vehicle.');
                    return;
                }
                const resp = await fetch(`${getUserDetailsBaseUrl()}/api/validate_vehicle`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        make: vehicleMake,
                        model: vehicleModel,
                        year: vehicleYearRaw ? Number(vehicleYearRaw) : undefined,
                    }),
                });
                const data = await resp.json().catch(() => ({}));
                if (data?.valid === false) {
                    const reason = data.invalid_reason || "That make/model combination doesn't look right.";
                    const sugg = Array.isArray(data.suggestions) && data.suggestions.length
                        ? ` Did you mean: ${data.suggestions.slice(0, 3).join(', ')}?`
                        : ' Please check the make and model.';
                    setVehicleConfirmError(reason + sugg);
                    return;
                }
            }
            setVehicleConfirmed(true);
        } catch {
            setVehicleConfirmError('Could not validate the vehicle right now. Please try again.');
        } finally {
            setIsConfirmingVehicle(false);
        }
    }

    async function handleCreate() {
        const nameError = getProfileNameError(form.customer_name);
        if (form.customer_name && nameError) {
            setError(nameError);
            return;
        }
        if (customerMode === 'existing' && !selectedCustomerKey) {
            setError('Select an existing customer or switch to New user.');
            return;
        }
        if (!form.issue_title.trim()) {
            setError('Issue is required.');
            return;
        }
        if (requiresShopSelection && !selectedPartnerShopId) {
            setError('Select a shop before creating a job or referral.');
            return;
        }
        if (!customerPhone && !form.customer_email.trim()) {
            setError('Enter a full phone number or email so the driver can sign in.');
            return;
        }
        // An existing customer's vehicle is already on file and the Vehicle section is hidden,
        // so ignore any form vehicle fields for that mode (don't send stale data).
        const {
            vehicleVin,
            vehicleYearRaw,
            vehicleMileageRaw,
            vehicleMake,
            vehicleModel,
            vehicleTrim,
            vehicleEngine,
            vehicleTransmission,
            vehicleDrivetrain,
            licensePlate,
            hasAnyVehicleField,
        } = customerMode === 'existing' ? {} : getReferralVehicleFieldState(form);
        if (vehicleVin && !REFERRAL_VIN_PATTERN.test(vehicleVin)) {
            setError('Enter a valid 17-character VIN (letters and numbers, no I, O, or Q), or clear the VIN field.');
            return;
        }
        if (hasAnyVehicleField && !vehicleVin && (!vehicleMake || !vehicleModel)) {
            setError('Vehicle details are optional, but if you add them, enter either a VIN or both make and model. Clear the vehicle fields to skip vehicle details.');
            return;
        }
        let vehicleYear = vehicleYearRaw ? Number(vehicleYearRaw) : undefined;
        const vehicleMileage = vehicleMileageRaw ? Number(vehicleMileageRaw) : undefined;
        if (vehicleMileage !== undefined && vehicleMileage > MAX_ODOMETER_MILES) {
            setError(ODOMETER_TOO_HIGH_MESSAGE);
            return;
        }
        // When a VIN is present it is authoritative (mirrors the chat Add Vehicle flow):
        // decode it, block if it isn't a real VIN, and use the decoded Year/Make/Model so a
        // format-valid but fake VIN (or edited details) can't be saved.
        let finalMake = vehicleMake, finalModel = vehicleModel, finalTrim = vehicleTrim;
        let finalEngine = vehicleEngine, finalTransmission = vehicleTransmission, finalDrivetrain = vehicleDrivetrain;
        if (vehicleVin) {
            setIsCreating(true);
            try {
                const resp = await fetch(`${getUserDetailsBaseUrl()}/api/decode_vin/${encodeURIComponent(vehicleVin)}`);
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok || data?.status !== 'success' || !data?.vehicle) {
                    setIsCreating(false);
                    setError(data?.error || "This VIN doesn't appear to be valid. Please check it, or clear the VIN and enter the details manually.");
                    return;
                }
                const v = data.vehicle;
                const decodedYear = Number(String(v.year || '').replace(/[^\d]/g, ''));
                if (decodedYear) vehicleYear = decodedYear;
                if (v.make) finalMake = v.make;
                if (v.model) finalModel = v.model;
                if (v.trim) finalTrim = v.trim;
                if (v.engine) finalEngine = v.engine;
                if (v.transmission) finalTransmission = v.transmission;
                if (v.drivetrain) finalDrivetrain = v.drivetrain;
            } catch {
                setIsCreating(false);
                setError('Could not validate the VIN right now. Please try again, or clear the VIN and enter the details manually.');
                return;
            }
        }
        setIsCreating(true);
        setError('');
        try {
            const result = await createShopReferral({
                customer_name: normalizeProfileName(form.customer_name),
                customer_phone: customerPhone,
                customer_email: form.customer_email.trim(),
                issue_title: form.issue_title.trim(),
                preliminary_issue: form.preliminary_issue.trim(),
                appointment_date: appointmentLabel,
                vehicle_vin: vehicleVin,
                vehicle_year: vehicleYear,
                vehicle_make: finalMake,
                vehicle_model: finalModel,
                vehicle_trim: finalTrim,
                vehicle_engine: finalEngine,
                vehicle_transmission: finalTransmission,
                vehicle_drivetrain: finalDrivetrain,
                vehicle_mileage: vehicleMileage,
                license_plate: licensePlate,
            }, selectedPartnerShopId ? { partnerShopId: selectedPartnerShopId } : {});
            setCreated(result);
        } catch (err) {
            setError(err?.message || 'Could not create referral link.');
        } finally {
            setIsCreating(false);
        }
    }

    async function handleCopy() {
        const url = created?.url || '';
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            setError('Could not copy the link.');
        }
    }

    async function handleGeneralLink() {
        if (requiresShopSelection && !selectedPartnerShopId) {
            setError('Select a shop before creating a general link.');
            return;
        }
        setIsLoadingGeneralLink(true);
        setGeneralCopied(false);
        setError('');
        try {
            const result = await getReusableShopReferralLink(selectedPartnerShopId ? { partnerShopId: selectedPartnerShopId } : {});
            if (!result?.url) throw new Error('Could not create the general referral link.');
            setGeneralLink(result.url);
            await navigator.clipboard.writeText(result.url);
            setGeneralCopied(true);
            setTimeout(() => setGeneralCopied(false), 1600);
        } catch (err) {
            setError(err?.message || 'Could not create the general referral link.');
        } finally {
            setIsLoadingGeneralLink(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4">
            <div className="w-full max-w-2xl overflow-hidden rounded-t-[28px] border border-[#d9d9d9] bg-white shadow-2xl sm:rounded-[28px]">
                <style>{`
                    .shop-referral-phone { width: 100% !important; }
                    .shop-referral-phone .form-control {
                        width: 100% !important;
                        height: 44px !important;
                        border-radius: 12px !important;
                        border: 1px solid #d9d9d9 !important;
                        color: #414141 !important;
                        font-size: 14px !important;
                        font-weight: 700 !important;
                        padding-left: 52px !important;
                    }
                    .shop-referral-phone .form-control:focus {
                        border-color: #D81F26 !important;
                        box-shadow: 0 0 0 2px rgba(216,31,38,0.14) !important;
                    }
                    .shop-referral-phone .flag-dropdown {
                        border: 1px solid #d9d9d9 !important;
                        border-radius: 12px 0 0 12px !important;
                        background: #F9F9F9 !important;
                    }
                    .shop-referral-phone .selected-flag {
                        width: 48px !important;
                        padding-left: 12px !important;
                        border-radius: 12px 0 0 12px !important;
                    }
                    .shop-referral-phone-dropdown {
                        border-radius: 14px !important;
                        border-color: #d9d9d9 !important;
                        box-shadow: 0 18px 48px rgba(34,34,34,0.18) !important;
                    }
                    .shop-referral-phone-dropdown .country {
                        font-size: 13px !important;
                        font-weight: 700 !important;
                    }
                `}</style>
                <div className="flex items-start justify-between gap-4 border-b border-[#d9d9d9] px-5 py-4">
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-lg font-black text-[#414141]">New job</p>
                            {created && <ReferralLinkDeliveryStatus delivery={created.delivery} />}
                        </div>
                        <p className="mt-1 text-sm font-semibold text-[#414141]">
                            Send the driver a CarFido intake link with this shop locked for the first job.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f7f7f7] text-[#414141]"
                        aria-label="Close referral modal"
                    >
                        x
                    </button>
                </div>

                <div className="max-h-[72vh] overflow-y-auto px-5 py-4">
                    {!created ? (
                        <div className="grid gap-4">
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-2.5">
                                <div className="min-w-0">
                                    <p className="text-sm font-black text-[#414141]">Reusable shop link</p>
                                    {generalLink ? (
                                        <p className="mt-1 break-all text-xs font-bold text-[#414141]">{generalLink}</p>
                                    ) : (
                                        <p className="mt-1 text-xs font-semibold text-[#414141]">Copy the permanent QR/front desk link for this shop.</p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleGeneralLink}
                                    disabled={isLoadingGeneralLink}
                                    className="h-9 shrink-0 rounded-xl border border-[#D9D9D9] bg-white px-3 text-xs font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2] disabled:cursor-wait disabled:opacity-60"
                                >
                                    {isLoadingGeneralLink ? 'Loading' : (generalCopied ? 'Copied' : 'Create general link')}
                                </button>
                            </div>
                            <div className="grid gap-2 rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-2 sm:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCustomerMode('new');
                                        setSelectedCustomerKey('');
                                        setCustomerSearch('');
                                        setDirectField('customer_name', '');
                                        setDirectField('customer_phone_digits', '');
                                        setDirectField('customer_email', '');
                                    }}
                                    className={`rounded-xl px-4 py-3 text-left transition ${customerMode === 'new' ? 'bg-[#414141] text-white shadow-sm' : 'bg-white text-[#414141] hover:bg-[#f5f5f5]'}`}
                                >
                                    <span className="block text-sm font-black">New user</span>
                                    <span className={`mt-0.5 block text-xs font-bold ${customerMode === 'new' ? 'text-white/80' : 'text-[#414141]'}`}>Create an intake link for a first-time driver.</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCustomerMode('existing');
                                        setError('');
                                    }}
                                    className={`rounded-xl px-4 py-3 text-left transition ${customerMode === 'existing' ? 'bg-[#414141] text-white shadow-sm' : 'bg-white text-[#414141] hover:bg-[#f5f5f5]'}`}
                                >
                                    <span className="block text-sm font-black">Existing user</span>
                                    <span className={`mt-0.5 block text-xs font-bold ${customerMode === 'existing' ? 'text-white/80' : 'text-[#414141]'}`}>Search customers saved from previous jobs.</span>
                                </button>
                            </div>

                            {customerMode === 'existing' && (
                                <section className="rounded-2xl border border-[#d9d9d9] bg-white p-3">
                                    <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                        Find customer
                                        <input
                                            value={customerSearch}
                                            onChange={(event) => setCustomerSearch(event.target.value)}
                                            placeholder="Search name, phone, email, or vehicle"
                                            className="h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26]"
                                        />
                                    </label>
                                    <div className="mt-3 max-h-44 space-y-2 overflow-y-auto">
                                        {filteredCustomers.length === 0 ? (
                                            <p className="rounded-xl bg-[#F9F9F9] px-3 py-3 text-sm font-bold text-[#414141]">
                                                No saved customers found yet.
                                            </p>
                                        ) : filteredCustomers.map((customer) => {
                                            const selected = selectedCustomerKey === customer.key;
                                            return (
                                                <button
                                                    key={customer.key}
                                                    type="button"
                                                    onClick={() => selectExistingCustomer(customer)}
                                                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${selected ? 'border-[#D81F26] bg-[#f6f6f6]' : 'border-[#d9d9d9] bg-white hover:border-[#414141]'}`}
                                                >
                                                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-black ${selected ? 'bg-[#D81F26] text-white' : 'bg-[#414141] text-white'}`}>
                                                        {(customer.name || 'C').slice(0, 1).toUpperCase()}
                                                    </span>
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-sm font-black text-[#414141]">{customer.name}</span>
                                                        <span className="mt-0.5 block truncate text-xs font-bold text-[#414141]">
                                                            {[formatPhoneLabel(customer.phone), customer.email, customer.vehicle].filter(Boolean).join(' / ')}
                                                        </span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}

                            <div className="grid gap-3 rounded-2xl border border-[#d9d9d9] bg-white p-3">
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Customer</p>
                                    <p className="mt-1 text-xs font-bold text-[#414141]">Phone or email <span className="text-[#b42318]">*</span> is required for OTP sign-in.</p>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                        Customer name
                                        <input value={form.customer_name} onChange={setField('customer_name')} placeholder="Amit" className="h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26]" />
                                    </label>
                                    <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                        Phone
                                        <PhoneInput
                                            country="us"
                                            value={form.customer_phone_digits}
                                            onChange={(value) => setDirectField('customer_phone_digits', digitsOnly(value))}
                                            enableSearch
                                            searchPlaceholder="Search country or code..."
                                            countryCodeEditable={false}
                                            containerClass="shop-referral-phone"
                                            dropdownClass="shop-referral-phone-dropdown"
                                            inputProps={{ autoComplete: 'tel' }}
                                        />
                                    </label>
                                    <label className="grid gap-1.5 text-sm font-black text-[#414141] sm:col-span-2">
                                        Email
                                        <input value={form.customer_email} onChange={setField('customer_email')} placeholder="driver@example.com" type="email" className="h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26]" />
                                    </label>
                                </div>
                            </div>

                            <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                <span>Issue <span className="text-[#b42318]">*</span></span>
                                <input value={form.issue_title} onChange={setField('issue_title')} placeholder="AC not cooling" className="h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26]" />
                            </label>
                            <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                Details
                                <textarea value={form.preliminary_issue} onChange={setField('preliminary_issue')} rows={3} placeholder="Customer says the AC blows warm at idle." className="rounded-xl border border-[#d9d9d9] px-3 py-2 text-sm font-semibold outline-none focus:border-[#D81F26]" />
                            </label>
                            {/* An existing customer's vehicle is already on file, so the Vehicle
                                section is only shown for a new user. */}
                            {customerMode !== 'existing' && (
                            <section className="rounded-2xl border border-[#d8d8d8] bg-[#f3f3f3] p-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Vehicle</p>
                                <p className="mt-1 text-xs font-bold text-[#414141]">Optional. Add what the shop already knows; the driver can confirm or add details after sign-in.</p>
                                <div className="mt-3 grid gap-3">
                                    <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            VIN
                                            <input
                                                value={form.vehicle_vin}
                                                onChange={setSanitizedField('vehicle_vin', sanitizeReferralVin)}
                                                placeholder="17-character VIN"
                                                maxLength={17}
                                                autoCapitalize="characters"
                                                className="h-11 rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-semibold outline-none focus:border-[#D81F26]"
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleVinLookup}
                                            disabled={isLookingUpVin}
                                            className="mt-auto h-11 rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-white disabled:cursor-wait disabled:text-[#9d9d9d]"
                                        >
                                            {isLookingUpVin ? 'Decoding' : 'Decode'}
                                        </button>
                                    </div>
                                    {vinLookupError && <p className="-mt-1 text-xs font-bold text-[#b42318]">{vinLookupError}</p>}
                                    {vinLockedFields.size > 0 && (
                                        <p className="-mt-1 text-xs font-semibold text-[#414141]">Details decoded from the VIN are locked. Clear or change the VIN to edit them manually.</p>
                                    )}
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Year
                                            <input
                                                value={form.vehicle_year}
                                                onChange={setSanitizedField('vehicle_year', (value) => sanitizeNumericField(value, 4))}
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength={4}
                                                placeholder="2019"
                                                disabled={vinLockedFields.has('vehicle_year')}
                                                className={`h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26] ${vinLockedFields.has('vehicle_year') ? 'cursor-not-allowed bg-[#f1f1f1] text-[#414141]' : 'bg-white'}`}
                                            />
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Make
                                            <input value={form.vehicle_make} onChange={setField('vehicle_make')} placeholder="Toyota" disabled={vinLockedFields.has('vehicle_make')} className={`h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26] ${vinLockedFields.has('vehicle_make') ? 'cursor-not-allowed bg-[#f1f1f1] text-[#414141]' : 'bg-white'}`} />
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Model
                                            <input value={form.vehicle_model} onChange={setField('vehicle_model')} placeholder="Camry" disabled={vinLockedFields.has('vehicle_model')} className={`h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26] ${vinLockedFields.has('vehicle_model') ? 'cursor-not-allowed bg-[#f1f1f1] text-[#414141]' : 'bg-white'}`} />
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Trim
                                            <input value={form.vehicle_trim} onChange={setField('vehicle_trim')} placeholder="XLE" className="h-11 rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-semibold outline-none focus:border-[#D81F26]" />
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141] sm:col-span-2">
                                            Engine
                                            <input value={form.vehicle_engine} onChange={setField('vehicle_engine')} placeholder="2.5L I4" disabled={vinLockedFields.has('vehicle_engine')} className={`h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26] ${vinLockedFields.has('vehicle_engine') ? 'cursor-not-allowed bg-[#f1f1f1] text-[#414141]' : 'bg-white'}`} />
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Transmission
                                            <select value={form.vehicle_transmission} onChange={setField('vehicle_transmission')} disabled={vinLockedFields.has('vehicle_transmission')} className={`h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26] ${vinLockedFields.has('vehicle_transmission') ? 'cursor-not-allowed bg-[#f1f1f1] text-[#414141]' : 'bg-white'}`}>
                                                <option value="">Select</option>
                                                <option value="Automatic">Automatic</option>
                                                <option value="Manual">Manual</option>
                                                <option value="CVT">CVT</option>
                                                <option value="EV">EV</option>
                                            </select>
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Drivetrain
                                            <select value={form.vehicle_drivetrain} onChange={setField('vehicle_drivetrain')} disabled={vinLockedFields.has('vehicle_drivetrain')} className={`h-11 rounded-xl border border-[#d9d9d9] px-3 text-sm font-semibold outline-none focus:border-[#D81F26] ${vinLockedFields.has('vehicle_drivetrain') ? 'cursor-not-allowed bg-[#f1f1f1] text-[#414141]' : 'bg-white'}`}>
                                                <option value="">Select</option>
                                                <option value="FWD">FWD</option>
                                                <option value="RWD">RWD</option>
                                                <option value="AWD">AWD</option>
                                                <option value="4WD">4WD</option>
                                            </select>
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            Mileage
                                            <input
                                                value={form.vehicle_mileage}
                                                onChange={setSanitizedField('vehicle_mileage', (value) => sanitizeNumericField(value, 7))}
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength={7}
                                                placeholder="54200"
                                                className="h-11 rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-semibold outline-none focus:border-[#D81F26]"
                                            />
                                        </label>
                                        <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                            License plate
                                            <input value={form.license_plate} onChange={setField('license_plate')} placeholder="ABC-1234" className="h-11 rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-semibold outline-none focus:border-[#D81F26]" />
                                        </label>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleConfirmVehicle}
                                            disabled={isConfirmingVehicle}
                                            className="h-10 rounded-xl border border-[#D9D9D9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2] disabled:cursor-wait disabled:opacity-60"
                                        >
                                            {isConfirmingVehicle ? 'Confirming…' : (vehicleConfirmed ? 'Re-confirm vehicle' : 'Confirm vehicle')}
                                        </button>
                                        {vehicleConfirmed && !vehicleConfirmError && (
                                            <span className="inline-flex items-center gap-1.5 text-sm font-black text-emerald-600">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                Vehicle confirmed
                                            </span>
                                        )}
                                    </div>
                                    {vehicleConfirmError && <p className="text-xs font-bold text-[#b42318]">{vehicleConfirmError}</p>}
                                </div>
                            </section>
                            )}
                            <section className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-3">
                                <div className="mb-2 flex items-center justify-between gap-3">
                                    <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#414141]">Appointment</p>
                                    {appointmentLabel && (
                                        <button
                                            type="button"
                                            onClick={() => setForm((prev) => ({ ...prev, appointment_date: '', appointment_time: '09:00' }))}
                                            className="text-xs font-black text-[#D81F26]"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                        Date
                                        <span className="relative h-12">
                                            <input
                                                ref={dateInputRef}
                                                type="date"
                                                lang="en-US"
                                                min={todayISODate()}
                                                value={form.appointment_date}
                                                onChange={setField('appointment_date')}
                                                onClick={openDatePicker}
                                                onFocus={openDatePicker}
                                                className="peer absolute inset-0 z-10 h-12 w-full cursor-pointer opacity-0"
                                                aria-label="Choose appointment date"
                                            />
                                            <span className="flex h-12 w-full items-center justify-between rounded-xl border border-[#d9d9d9] bg-white px-3 text-sm font-black text-[#414141] peer-focus:border-[#D81F26] peer-focus:ring-2 peer-focus:ring-[#D81F26]/15">
                                                <span>{appointmentDateLabel || 'Select date'}</span>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" />
                                                    <path d="M16 2v4M8 2v4M3 10h18" />
                                                </svg>
                                            </span>
                                        </span>
                                    </label>
                                    <label className="grid gap-1.5 text-sm font-black text-[#414141]">
                                        Time
                                        <AppointmentClockPicker
                                            id="referral-appointment-time"
                                            value={form.appointment_time}
                                            onChange={(value) => setDirectField('appointment_time', value)}
                                            align="left"
                                            openUp
                                        />
                                    </label>
                                </div>
                                <p className="mt-2 text-xs font-bold text-[#414141]">
                                    {appointmentDateLabel ? `Selected: ${appointmentDateLabel}${appointmentTimeLabel ? ` / ${appointmentTimeLabel}` : ''}` : 'Optional. Add a date and time if the shop already discussed a visit.'}
                                </p>
                            </section>
                            {error && <p className="rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{error}</p>}
                            <button
                                type="button"
                                onClick={handleCreate}
                                disabled={isCreating}
                                className="mt-1 h-11 rounded-xl bg-[#D81F26] px-4 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-70"
                            >
                                {isCreating ? 'Creating & sending…' : 'Create link'}
                            </button>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            <div className="rounded-2xl border border-[#bde7d5] bg-[#ecfff5] px-4 py-3">
                                <p className="text-sm font-black text-[#087a43]">Referral link ready</p>
                                <p className="mt-1 break-all text-xs font-bold text-[#414141]">{created.url}</p>
                            </div>
                            {created.general_url && (
                                <div className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3">
                                    <p className="text-sm font-black text-[#414141]">General QR link</p>
                                    <p className="mt-1 break-all text-xs font-bold text-[#414141]">{created.general_url}</p>
                                </div>
                            )}
                            {error && <p className="rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-sm font-bold text-[#b42318]">{error}</p>}
                            <div className="grid gap-2 sm:grid-cols-2">
                                <button type="button" onClick={handleCopy} className="h-11 rounded-xl bg-[#D81F26] px-4 text-sm font-black text-white hover:bg-[#B8181E]">
                                    {copied ? 'Copied' : 'Copy link'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCreated(null);
                                        resetForm();
                                    }}
                                    className="h-11 rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141]"
                                >
                                    Create another job
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ShopDashboardPage() {
    const navigate = useNavigate();
    const requestedRequestId = String(new URLSearchParams(window.location.search).get('request_id') || '').trim();
    const pageScrollRef = useRef(null);
    const estimateEditorRef = useRef(null);
    const shouldScrollEstimateEditorRef = useRef(false);
    const [requests, setRequests] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [shops, setShops] = useState([]);
    const [selectedRequestId, setSelectedRequestId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [jobView, setJobView] = useState('active');
    const [statusFilter, setStatusFilter] = useState('all');
    const [estimateFilter, setEstimateFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [activeDetailTab, setActiveDetailTab] = useState('overview');
    const [statusNotice, setStatusNotice] = useState('');
    const [editingEstimateRequestId, setEditingEstimateRequestId] = useState('');
    const [isReferralInviteOpen, setIsReferralInviteOpen] = useState(false);
    const [isDemoAdminView, setIsDemoAdminView] = useState(false);
    const [isRescheduleRequestOpen, setIsRescheduleRequestOpen] = useState(false);
    const [rescheduleReason, setRescheduleReason] = useState('');
    const [isRequestingReschedule, setIsRequestingReschedule] = useState(false);
    const [shopFilter, setShopFilter] = useState('all');
    const [copyingReferralId, setCopyingReferralId] = useState('');
    const [copiedReferralId, setCopiedReferralId] = useState('');
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [declineDetails, setDeclineDetails] = useState('');
    const [declineError, setDeclineError] = useState('');

    async function refreshRequests(options = {}) {
        const silent = Boolean(options.silent);
        if (!silent) setIsLoading(true);
        setError('');
        try {
            const [result, referralRows] = await Promise.all([
                listShopDashboardRepairRequests({
                    partner_shop_id: shopFilter === 'all' ? '' : shopFilter,
                    status: 'all',
                    estimate_state: estimateFilter,
                    q: search.trim(),
                }),
                listShopReferrals(shopFilter !== 'all' ? { partnerShopId: shopFilter } : {}).catch(() => []),
            ]);
            setRequests(result.requests.map((request) => {
                const referral = referralRows.find((row) => referralMatchesConvertedLead(row, request));
                return referral ? { ...request, referral: { ...referral, ...request.referral } } : request;
            }));
            setReferrals(referralRows);
            setShops(result.shops);
            setIsDemoAdminView(Boolean(result.demoAdminEnabled));
            setSelectedRequestId((current) => {
                if (requestedRequestId && result.requests.some((request) => request.request_id === requestedRequestId)) {
                    return requestedRequestId;
                }
                return result.requests.some((request) => request.request_id === current) ? current : '';
            });
        } catch (err) {
            setError(err?.message || 'Could not load partner requests.');
            setReferrals([]);
        } finally {
            if (!silent) setIsLoading(false);
        }
    }

    useEffect(() => {
        refreshRequests();
    }, [shopFilter, estimateFilter, search, requestedRequestId]); // eslint-disable-line react-hooks/exhaustive-deps

    useShopDashboardRequestUpdates({
        enabled: true,
        onUpdate: () => refreshRequests({ silent: true }),
    });

    useEffect(() => {
        setActiveDetailTab('overview');
        setStatusNotice('');
        setEditingEstimateRequestId('');
        setIsRescheduleRequestOpen(false);
        setRescheduleReason('');
        setIsDeclineModalOpen(false);
        setDeclineReason('');
        setDeclineDetails('');
        setDeclineError('');
    }, [selectedRequestId]);

    useEffect(() => {
        if (jobView !== 'active') setStatusFilter('all');
    }, [jobView]);

    useEffect(() => {
        if (!requestedRequestId || selectedRequestId !== requestedRequestId) return;
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete('request_id');
        const nextSearch = nextParams.toString();
        window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`
        );
    }, [requestedRequestId, selectedRequestId]);

    const scrollEstimateEditorIntoView = useCallback((attempt = 0) => {
        const afterPaint = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
        afterPaint(() => {
            const editor = estimateEditorRef.current;
            if (!editor) {
                if (attempt < 6) scrollEstimateEditorIntoView(attempt + 1);
                return;
            }
            shouldScrollEstimateEditorRef.current = false;
            const scrollContainer = pageScrollRef.current;
            if (scrollContainer?.scrollTo) {
                const containerRect = scrollContainer.getBoundingClientRect();
                const editorRect = editor.getBoundingClientRect();
                const top = scrollContainer.scrollTop + editorRect.top - containerRect.top - 16;
                scrollContainer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
            } else {
                editor.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            const firstField = editor.querySelector('textarea, input, select');
            firstField?.focus?.({ preventScroll: true });
        });
    }, []);

    useEffect(() => {
        if (!shouldScrollEstimateEditorRef.current) return;
        if (activeDetailTab !== 'estimate') return;
        if (!editingEstimateRequestId || editingEstimateRequestId !== selectedRequestId) return;

        scrollEstimateEditorIntoView();
    }, [activeDetailTab, editingEstimateRequestId, scrollEstimateEditorIntoView, selectedRequestId]);

    const selectedRequest = useMemo(
        () => requests.find((request) => request.request_id === selectedRequestId) || null,
        [requests, selectedRequestId]
    );
    const selectedWorkflow = useMemo(
        () => (selectedRequest ? getLeadWorkflow(selectedRequest) : null),
        [selectedRequest]
    );
    const selectedRequestMessages = useMemo(
        () => (selectedRequest ? buildPartnerRequestConversationMessages(selectedRequest, { driverLabel: 'Driver' }) : []),
        [selectedRequest]
    );
    const selectedShopReadyOverview = useMemo(
        () => (selectedRequest ? getShopReadyReportOverview(selectedRequest) : null),
        [selectedRequest]
    );
    const selectedTicketLabel = selectedRequest ? getTicketLabel(selectedRequest) : '';

    const convertedReferralLeads = useMemo(() => {
        const matches = new Map();
        referrals.forEach((referral) => {
            const referralId = String(referral?.referral_id || '').trim();
            if (!referralId) return;
            const matchingRequest = requests.find((request) => referralMatchesConvertedLead(referral, request));
            if (matchingRequest) matches.set(referralId, matchingRequest);
        });
        return matches;
    }, [referrals, requests]);

    const activeReferrals = useMemo(() => {
        const query = search.trim().toLowerCase();
        return referrals
            .filter((referral) => ACTIVE_REFERRAL_STATUSES.has(String(referral?.status || '').toLowerCase()))
            .filter((referral) => {
                if (['with_estimate', 'estimate_sent'].includes(estimateFilter)) return false;
                const linkedRequest = convertedReferralLeads.get(String(referral?.referral_id || '').trim());
                if (referral.linked_request_id || linkedRequest) return false;
                if (!query) return true;
                return [
                    referral?.customer_name,
                    referral?.customer_email,
                    referral?.customer_phone,
                    referral?.issue_title,
                    referral?.preliminary_issue,
                    referral?.appointment_date,
                    referral?.shop_reference_number,
                    getTicketLabel(linkedRequest),
                ].some((value) => String(value || '').toLowerCase().includes(query));
            });
    }, [convertedReferralLeads, estimateFilter, referrals, search]);

    const activeRequests = useMemo(
        () => requests.filter((request) => getJobViewBucket(request) === 'active'),
        [requests]
    );
    const completedRequests = useMemo(
        () => requests.filter((request) => getJobViewBucket(request) === 'completed'),
        [requests]
    );
    const rejectedRequests = useMemo(
        () => requests.filter((request) => getJobViewBucket(request) === 'rejected'),
        [requests]
    );
    const visibleRequests = useMemo(
        () => requests.filter((request) => requestMatchesStatusFilter(request, statusFilter, jobView)),
        [jobView, requests, statusFilter]
    );

    const visibleReferrals = useMemo(
        () => (jobView === 'active' && statusFilter === 'all' ? activeReferrals : []),
        [activeReferrals, jobView, statusFilter]
    );

    const inboxItems = useMemo(() => {
        const leadItems = visibleRequests.map((request) => ({
            kind: 'lead',
            key: request.referral?.referral_id ? `job-${request.referral.referral_id}` : `lead-${request.request_id}`,
            createdAt: request.referral?.created_at || request.created_at || '',
            createdMs: getTimestampMs(request.referral?.created_at || request.created_at),
            request,
        }));
        const intakeItems = visibleReferrals.map((referral) => ({
            kind: 'intake',
            key: `job-${referral.referral_id}`,
            createdAt: referral.created_at || '',
            createdMs: getTimestampMs(referral.created_at),
            referral,
            linkedRequest: convertedReferralLeads.get(String(referral?.referral_id || '').trim()) || null,
        }));
        return [...leadItems, ...intakeItems].sort((left, right) => {
            const diff = right.createdMs - left.createdMs;
            if (diff) return diff;
            return String(right.key).localeCompare(String(left.key));
        });
    }, [convertedReferralLeads, visibleRequests, visibleReferrals]);

    const partnerShops = useMemo(() => shops.filter((shop) => shop.shop_type !== 'nearby'), [shops]);
    const selectedAdminShopId = isDemoAdminView && shopFilter !== 'all' ? shopFilter : '';

    async function handleStatus(nextStatus, notes = '') {
        if (!selectedRequest) return;
        if (selectedRequest.request_status === nextStatus) return;
        if (nextStatus === 'declined' && !String(notes || '').trim()) {
            setDeclineError('');
            setIsDeclineModalOpen(true);
            return;
        }
        setIsUpdating(true);
        setError('');
        setStatusNotice('');
        try {
            const updated = String(notes || '').trim()
                ? await updateShopDashboardRepairRequestStatus(selectedRequest.request_id, nextStatus, notes)
                : await updateShopDashboardRepairRequestStatus(selectedRequest.request_id, nextStatus);
            setRequests((prev) => prev.map((request) => request.request_id === updated.request_id ? updated : request));
            if (nextStatus === 'declined') {
                setIsDeclineModalOpen(false);
                setDeclineReason('');
                setDeclineDetails('');
                setDeclineError('');
            }
            setStatusNotice(`Lead moved to ${getPartnerStatusLabel(updated.request_status, 'shop')}.`);
        } catch (err) {
            setError(err?.message || 'Could not update status.');
        } finally {
            setIsUpdating(false);
        }
    }

    function handleSubmitDecline() {
        const note = buildDeclineReasonNote(declineReason, declineDetails);
        if (!note) {
            setDeclineError('Choose a decline reason.');
            return;
        }
        setDeclineError('');
        handleStatus('declined', note);
    }

    function handleEstimateSent(updated) {
        setRequests((prev) => prev.map((request) => request.request_id === updated.request_id ? updated : request));
        setSelectedRequestId(updated.request_id);
        setEditingEstimateRequestId('');
        setStatusNotice('Estimate sent to the driver.');
    }

    function handleEditEstimate(requestId) {
        shouldScrollEstimateEditorRef.current = true;
        setActiveDetailTab('estimate');
        setEditingEstimateRequestId(requestId);
    }

    function handleServiceNotesUpdated(updated, notice = '') {
        if (!updated) return;
        setRequests((prev) => prev.map((request) => request.request_id === updated.request_id ? updated : request));
        setSelectedRequestId(updated.request_id);
        if (notice) setStatusNotice(notice);
    }

    async function handleShopMessage(message) {
        if (!selectedRequest) return;
        const updated = await sendShopDashboardRepairRequestMessage(selectedRequest.request_id, message);
        if (!updated) return;
        setRequests((prev) => prev.map((request) => request.request_id === updated.request_id ? updated : request));
        setSelectedRequestId(updated.request_id);
        setStatusNotice('Message sent to the driver.');
    }

    async function handleRequestReschedule() {
        if (!selectedRequest?.request_id || isRequestingReschedule) return;
        setIsRequestingReschedule(true);
        setError('');
        setStatusNotice('');
        try {
            const updated = await requestShopRepairRequestReschedule(selectedRequest.request_id, {
                reason: rescheduleReason,
            });
            if (updated?.request_id) {
                setRequests((prev) => prev.map((request) => request.request_id === updated.request_id ? updated : request));
                setSelectedRequestId(updated.request_id);
            }
            setRescheduleReason('');
            setIsRescheduleRequestOpen(false);
            setStatusNotice('Reschedule request sent to the driver.');
        } catch (err) {
            setError(err?.message || 'Could not send reschedule request.');
        } finally {
            setIsRequestingReschedule(false);
        }
    }

    function handlePrimaryAction() {
        if (!selectedWorkflow?.action) return;
        if (selectedWorkflow.action.type === 'tab') {
            setActiveDetailTab(selectedWorkflow.action.tab);
            return;
        }
        handleStatus(selectedWorkflow.action.status);
    }

    async function handleCopyReferralLink(referral) {
        const referralId = String(referral?.referral_id || '');
        if (!referralId) return;
        setCopyingReferralId(referralId);
        setStatusNotice('');
        setError('');
        try {
            let url = referral?.referral_url || '';
            let updatedReferral = null;
            if (!url) {
                const result = await getShopReferralLink(referralId, selectedAdminShopId ? { partnerShopId: selectedAdminShopId } : {});
                url = result?.url || result?.referral?.referral_url || '';
                updatedReferral = result?.referral || null;
            }
            if (!url) throw new Error('Referral link is unavailable.');
            await navigator.clipboard.writeText(url);
            if (updatedReferral) {
                setReferrals((prev) => prev.map((item) => (
                    String(item?.referral_id || '') === referralId ? { ...item, ...updatedReferral } : item
                )));
            }
            setCopiedReferralId(referralId);
            setStatusNotice('Referral link copied.');
            setTimeout(() => setCopiedReferralId((current) => (current === referralId ? '' : current)), 1600);
        } catch (err) {
            setError(err?.message || 'Could not copy referral link.');
        } finally {
            setCopyingReferralId('');
        }
    }

    const activeJobCount = activeRequests.length + activeReferrals.length;
    const newLeadCount = activeRequests.filter((request) => getJobStageFilterBucket(request) === 'new_lead').length;
    const visibleScheduled = activeRequests.filter((request) => getJobStageFilterBucket(request) === 'scheduled').length;
    const visibleInService = activeRequests.filter((request) => getJobStageFilterBucket(request) === 'in_service').length;
    const visibleReady = activeRequests.filter((request) => getJobStageFilterBucket(request) === 'ready_for_pickup').length;
    const visibleInboxCount = inboxItems.length;
    const jobViewMeta = JOB_VIEW_TABS.find((tab) => tab.key === jobView) || JOB_VIEW_TABS[0];
    const inboxCountLabel = jobView === 'active'
        ? `${visibleInboxCount} visible active jobs`
        : `${visibleInboxCount} visible ${jobViewMeta.label.toLowerCase()}`;
    const canRequestSelectedReschedule = Boolean(selectedRequest?.scheduled_start_at)
        && String(selectedRequest?.request_status || '').toLowerCase() === 'scheduled';

    return (
        <>
        <main ref={pageScrollRef} className="relative h-full w-full overflow-y-auto bg-[#F9F9F9] text-[#414141] custom-scrollbar">
            <div className={`mx-auto w-full space-y-5 px-4 pb-10 pt-8 md:px-6 md:pt-10 ${selectedRequest ? 'max-w-6xl' : 'max-w-5xl'}`}>
                {selectedRequest ? (
                    <header className="animate-in fade-in slide-in-from-top-4 duration-700">
                        <button
                            type="button"
                            onClick={() => setSelectedRequestId('')}
                            className="mb-4 inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] shadow-sm transition hover:border-[#414141] hover:bg-[#F9F9F9]"
                        >
                            <Icon className="h-4 w-4"><path d="m15 18-6-6 6-6" /></Icon>
                            Back to leads
                        </button>
                        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <StatusBadge status={selectedRequest.request_status} />
                                    {selectedTicketLabel && (
                                        <span className="inline-flex items-center rounded-full border border-[#d9d9d9] bg-white px-2 py-0.5 text-xs font-black text-[#414141]">
                                            {selectedTicketLabel}
                                        </span>
                                    )}
                                    <span className="text-xs font-semibold text-[#414141]">
                                        Opened {getRequestAge(selectedRequest.created_at)} ago
                                    </span>
                                </div>
                                <h1 className="mt-2 max-w-4xl text-xl font-black leading-snug text-black md:text-2xl">
                                    {getRequestTitle(selectedRequest)}
                                </h1>
                                <p className="mt-2 max-w-3xl text-sm font-semibold leading-relaxed text-[#414141]">
                                    {getVehicleName(selectedRequest)} / {getCustomerName(selectedRequest)} / {selectedRequest.partner_shop?.name || 'Partner shop'}
                                </p>
                            </div>
                            <div className="flex shrink-0 flex-wrap gap-2 self-start md:self-auto">
                                {selectedWorkflow?.action && (
                                    <button
                                        type="button"
                                        onClick={handlePrimaryAction}
                                        disabled={isUpdating}
                                        className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-full bg-[#D81F26] px-5 text-sm font-black text-white shadow-sm transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {selectedWorkflow.action.type === 'tab' && selectedWorkflow.action.tab === 'messages' ? (
                                            <Icon className="h-4 w-4"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></Icon>
                                        ) : selectedWorkflow.action.type === 'tab' ? (
                                            <Icon className="h-4 w-4"><path d="M9 11h6M9 15h4" /><path d="M16 3H8a2 2 0 0 0-2 2v16l6-3 6 3V5a2 2 0 0 0-2-2z" /></Icon>
                                        ) : (
                                            <Icon className="h-4 w-4"><path d="M20 6 9 17l-5-5" /></Icon>
                                        )}
                                        {isUpdating && selectedWorkflow.action.type === 'status' ? 'Updating' : selectedWorkflow.action.label}
                                    </button>
                                )}
                                {selectedWorkflow?.action?.tab !== 'messages' && (
                                    <button
                                        type="button"
                                        onClick={() => setActiveDetailTab('messages')}
                                        className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] shadow-sm transition hover:border-[#414141] hover:bg-[#F9F9F9]"
                                    >
                                        <Icon className="h-4 w-4"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></Icon>
                                        Chat
                                        {selectedRequestMessages.length > 0 && (
                                            <span className="rounded-full bg-[#f5f5f5] px-1.5 py-0.5 text-[10px] text-[#414141]">
                                                {selectedRequestMessages.length}
                                            </span>
                                        )}
                                    </button>
                                )}
                                {canRequestSelectedReschedule && (
                                    <button
                                        type="button"
                                        onClick={() => setIsRescheduleRequestOpen((open) => !open)}
                                        className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-full border border-[#f7d58f] bg-white px-5 text-sm font-black text-[#936000] shadow-sm transition hover:border-[#e5b342] hover:bg-[#fff8e8]"
                                    >
                                        <Icon className="h-4 w-4"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M3 21v-5h5" /><path d="M3 12a9 9 0 0 1 15.74-5.74L21 8" /><path d="M16 8h5V3" /></Icon>
                                        Request reschedule
                                    </button>
                                )}
                            </div>
                        </div>
                        {canRequestSelectedReschedule && isRescheduleRequestOpen && (
                            <div className="mt-4 rounded-2xl border border-[#f7d58f] bg-[#fff8e8] p-4">
                                <label className="text-xs font-black uppercase tracking-[0.08em] text-[#936000]" htmlFor="shop-reschedule-reason">
                                    Optional reason
                                </label>
                                <textarea
                                    id="shop-reschedule-reason"
                                    value={rescheduleReason}
                                    onChange={(event) => setRescheduleReason(event.target.value)}
                                    placeholder="Tell the driver why a new time would help"
                                    rows={3}
                                    className="mt-2 min-h-[88px] w-full resize-y rounded-xl border border-[#f7d58f] bg-white px-3 py-2 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26]"
                                />
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleRequestReschedule}
                                        disabled={isRequestingReschedule}
                                        className="inline-flex min-h-[38px] items-center rounded-full bg-[#D81F26] px-4 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        {isRequestingReschedule ? 'Sending...' : 'Send request'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsRescheduleRequestOpen(false);
                                            setRescheduleReason('');
                                        }}
                                        className="inline-flex min-h-[38px] items-center rounded-full border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9]"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </header>
                ) : (
                    <header className="flex flex-col gap-5 animate-in fade-in slide-in-from-top-4 duration-700 md:flex-row md:items-end md:justify-between">
                        <div>
                            <h1 className="text-2xl font-black tracking-tight text-black md:text-[28px]">{jobViewMeta.label}</h1>
                            <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#414141]">
                                {inboxCountLabel}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:min-w-[720px] md:grid-cols-7">
                            <button
                                type="button"
                                onClick={() => setIsReferralInviteOpen(true)}
                                className="flex min-h-[58px] items-center justify-center rounded-[18px] bg-[#D81F26] px-4 text-white shadow-sm transition hover:bg-[#B8181E]"
                            >
                                <span className="inline-flex items-center gap-1 text-base font-black leading-none">
                                    <span className="text-xl leading-none">+</span>
                                    New Job
                                </span>
                            </button>
                            <SmallMetric label="Active Jobs" value={activeJobCount} emphasis />
                            <SmallMetric label="New Leads" value={newLeadCount} emphasis />
                            <SmallMetric label="Scheduled" value={visibleScheduled} />
                            <SmallMetric label="In Service" value={visibleInService} />
                            <SmallMetric label="Ready" value={visibleReady} />
                            <SmallMetric label="Closed" value={completedRequests.length + rejectedRequests.length} />
                        </div>
                    </header>
                )}

                {!selectedRequest && <section className="rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {JOB_VIEW_TABS.map((tab) => {
                                const count = tab.key === 'active'
                                    ? activeRequests.length + activeReferrals.length
                                    : tab.key === 'completed'
                                        ? completedRequests.length
                                        : rejectedRequests.length;
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => setJobView(tab.key)}
                                        className={`inline-flex min-h-[38px] shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-black transition ${
                                            jobView === tab.key
                                                ? 'border-[#414141] bg-[#414141] text-white shadow-sm'
                                                : 'border-[#d9d9d9] bg-white text-[#414141] hover:border-[#414141] hover:bg-[#F9F9F9]'
                                        }`}
                                    >
                                        {tab.label}
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                                            jobView === tab.key ? 'bg-white/20 text-white' : 'bg-[#f5f5f5] text-[#414141]'
                                        }`}>
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <span className="flex min-h-[42px] flex-1 items-center gap-2 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-[#414141] focus-within:border-[#D81F26] focus-within:bg-white">
                            <Icon className="h-4 w-4 shrink-0"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></Icon>
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search customer, vehicle, shop, plate, issue"
                                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-black placeholder:text-[#414141] outline-none"
                            />
                        </span>
                        {jobView === 'active' && (
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {STAGE_FILTERS.map((filter) => (
                                    <button
                                        key={filter.key}
                                        type="button"
                                        onClick={() => setStatusFilter(filter.key)}
                                        className={`inline-flex min-h-[34px] shrink-0 items-center justify-center rounded-full border px-4 text-xs font-black transition ${
                                            statusFilter === filter.key
                                                ? 'border-[#414141] bg-[#414141] text-white shadow-sm'
                                                : 'border-[#d9d9d9] bg-white text-[#535353] hover:border-[#414141] hover:bg-[#F9F9F9]'
                                        }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {isDemoAdminView && <div className="mt-3 grid gap-2 md:grid-cols-[minmax(180px,1fr)_minmax(170px,220px)_auto]">
                        <select
                            value={shopFilter}
                            onChange={(event) => setShopFilter(event.target.value)}
                            className="min-h-[40px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:bg-white"
                        >
                            <option value="all">All shops</option>
                            {partnerShops.map((shop) => (
                                <option key={shop.partner_shop_id} value={shop.partner_shop_id}>{shop.name}</option>
                            ))}
                        </select>
                        <select
                            value={estimateFilter}
                            onChange={(event) => setEstimateFilter(event.target.value)}
                            className="min-h-[40px] rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:bg-white"
                        >
                            {ESTIMATE_FILTERS.map((filter) => (
                                <option key={filter.key} value={filter.key}>{filter.label}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => refreshRequests()}
                            className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F9F9F9]"
                        >
                            Refresh
                        </button>
                    </div>}
                </section>}

                {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                        {error}
                    </div>
                )}

                <div>
                    <section aria-hidden={Boolean(selectedRequest)} className={selectedRequest ? 'hidden' : 'space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500'}>
                        {isLoading ? (
                            [1, 2, 3].map((item) => (
                                <div key={item} className="h-28 animate-pulse rounded-2xl border border-[#d9d9d9] bg-white" />
                            ))
                        ) : inboxItems.length ? (
                            inboxItems.map((item) => (
                                item.kind === 'intake' ? (
                                    <ReferralIntakeRow
                                        key={item.key}
                                        referral={item.referral}
                                        linkedRequest={item.linkedRequest}
                                        onCopyLink={handleCopyReferralLink}
                                        isCopying={copyingReferralId === String(item.referral?.referral_id || '')}
                                        copied={copiedReferralId === String(item.referral?.referral_id || '')}
                                    />
                                ) : (
                                    <RequestRow
                                        key={item.key}
                                        request={item.request}
                                        active={selectedRequest?.request_id === item.request.request_id}
                                        onClick={(request) => setSelectedRequestId(request.request_id)}
                                    />
                                )
                            ))
                        ) : (
                            <div className="rounded-2xl border border-[#d9d9d9] bg-white p-8 text-center shadow-sm">
                                <Icon className="mx-auto h-8 w-8 text-[#8C8C8C]"><path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" /></Icon>
                                <p className="mt-3 text-sm font-black text-[#414141]">No matching leads</p>
                                <p className="mt-1 text-xs font-medium text-[#414141]">Request an estimate from a diagnostic report or adjust filters.</p>
                            </div>
                        )}
                    </section>

                    <section className={selectedRequest ? 'block' : 'hidden'}>
                        {!selectedRequest ? (
                            <div className="flex min-h-[360px] items-center justify-center rounded-2xl border border-[#d9d9d9] bg-white text-sm font-medium text-[#414141] shadow-sm">
                                Select a lead.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <StatusProcess
                                    request={selectedRequest}
                                    isUpdating={isUpdating}
                                    onStatus={handleStatus}
                                    onOpenTab={setActiveDetailTab}
                                />

                                {selectedRequest.referral && (
                                    <DetailSection title="Referral intake">
                                        <p className="text-sm font-black text-[#414141]">{selectedRequest.referral.issue_title}</p>
                                        {selectedRequest.referral.preliminary_issue && <p className="mt-2 text-sm text-[#414141]">{selectedRequest.referral.preliminary_issue}</p>}
                                        {selectedRequest.referral.appointment_date && <p className="mt-2 text-sm font-bold">{selectedRequest.referral.appointment_date}</p>}
                                        {selectedRequest.referral.shop_reference_number && <p className="mt-2 text-sm">Ref {selectedRequest.referral.shop_reference_number}</p>}
                                        <div className="mt-3"><ReferralProgress referral={selectedRequest.referral} /></div>
                                    </DetailSection>
                                )}

                                {statusNotice && (
                                    <div className="rounded-2xl border border-[#bde7d5] bg-[#ecfff5] px-4 py-3 text-sm font-black text-[#087a43]">
                                        {statusNotice}
                                    </div>
                                )}

                                <div className="overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm">
                                    <div className="border-b border-[#d9d9d9] bg-white px-4 py-3">
                                        <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-[#d9d9d9] bg-white p-1.5 shadow-sm">
                                            {DETAIL_TABS.map((tab) => (
                                                <button
                                                    key={tab.key}
                                                    type="button"
                                                    onClick={() => setActiveDetailTab(tab.key)}
                                                    className={`min-h-[34px] shrink-0 rounded-lg px-4 text-xs font-black transition ${
                                                        activeDetailTab === tab.key
                                                            ? 'bg-[#414141] text-white shadow-sm'
                                                            : 'text-[#414141] hover:bg-[#F9F9F9] hover:text-black'
                                                    }`}
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {activeDetailTab === 'overview' && (
                                            <div className="grid gap-4 xl:grid-cols-2">
                                                <DetailSection title="Customer" className="shadow-none">
                                                    <p className="text-base font-black text-[#414141]">{getCustomerName(selectedRequest)}</p>
                                                    <p className="mt-1 text-sm font-medium text-[#414141]">{getCustomerPhoneLabel(selectedRequest)}</p>
                                                </DetailSection>
                                                <DetailSection title="Vehicle" className="shadow-none">
                                                    <p className="text-base font-black text-[#414141]">{getVehicleName(selectedRequest)}</p>
                                                    <div className="mt-2 grid gap-1.5 text-sm font-medium text-[#414141] sm:grid-cols-2">
                                                        <span>Engine: {selectedRequest.vehicle?.engine || 'Unknown'}</span>
                                                        <span>Mileage: {selectedRequest.vehicle?.mileage || 'Unknown'}</span>
                                                        <span>VIN: {selectedRequest.vehicle?.vin || 'Unknown'}</span>
                                                        <span>Plate: {selectedRequest.vehicle?.plate_number || 'Unknown'}</span>
                                                    </div>
                                                </DetailSection>
                                                <DetailSection title="Service History" className="shadow-none xl:col-span-2">
                                                    <ServiceHistoryPanel entries={selectedRequest.service_history || []} />
                                                </DetailSection>
                                                <DetailSection title="Shop-ready report" className="shadow-none xl:col-span-2">
                                                    <ShopReadyOverview
                                                        overview={selectedShopReadyOverview}
                                                    />
                                                </DetailSection>
                                                <DetailSection title="Intake media" className="shadow-none xl:col-span-2">
                                                    <IntakeMediaPanel media={selectedRequest.intake_media || []} />
                                                </DetailSection>
                                                <DetailSection title="Estimate" className="shadow-none xl:col-span-2">
                                                    {selectedRequest.latest_estimate ? (
                                                        <div className="space-y-3">
                                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                                <p className="text-lg font-black text-[#414141]">
                                                                    {formatPartnerMoney(getEstimateAuthorizationSummary(selectedRequest.latest_estimate).proposed_total, selectedRequest.latest_estimate.currency)}
                                                                </p>
                                                                <EstimateAuthorizationBadge estimate={selectedRequest.latest_estimate} />
                                                            </div>
                                                            <EstimateAuthorizationTotals estimate={selectedRequest.latest_estimate} />
                                                            <EstimateAuthorizationNotice estimate={selectedRequest.latest_estimate} />
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm font-medium text-[#414141]">No estimate has been sent for this lead.</p>
                                                    )}
                                                </DetailSection>
                                                {(selectedRequest.request_status === 'declined' || selectedRequest.latest_estimate?.estimate_status === 'declined') && (
                                                    <DetailSection title="Decline note" className="shadow-none xl:col-span-2">
                                                        <div className="rounded-2xl border border-[#ffd2d2] bg-[#fff1f1] px-4 py-3">
                                                            <p className="text-sm font-black text-[#b42318]">
                                                                {getDeclineReason(selectedRequest) || 'Driver did not add a reason.'}
                                                            </p>
                                                        </div>
                                                    </DetailSection>
                                                )}
                                            </div>
                                        )}

                                        {activeDetailTab === 'estimate' && (
                                            <div className="space-y-4">
                                                <DetailSection title={selectedRequest.latest_estimate ? 'Latest estimate' : 'Estimate'} className="shadow-none">
                                                    {selectedRequest.latest_estimate ? (
                                                        <>
                                                            <div className="flex items-center justify-between gap-3">
                                                                <p className="text-lg font-black text-[#414141]">
                                                                    {formatPartnerMoney(getEstimateAuthorizationSummary(selectedRequest.latest_estimate).proposed_total, selectedRequest.latest_estimate.currency)}
                                                                </p>
                                                                <div className="flex flex-wrap items-center justify-end gap-2">
                                                                    <EstimateAuthorizationBadge estimate={selectedRequest.latest_estimate} />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleEditEstimate(selectedRequest.request_id)}
                                                                        className="inline-flex h-8 items-center justify-center rounded-xl border border-[#d8d8d8] bg-white px-3 text-xs font-black text-[#414141] hover:border-[#D81F26] hover:text-[#D81F26]"
                                                                    >
                                                                        Edit estimate
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3">
                                                                <EstimateAuthorizationTotals estimate={selectedRequest.latest_estimate} />
                                                            </div>
                                                            <EstimateAuthorizationNotice estimate={selectedRequest.latest_estimate} className="mt-3" />
                                                            {selectedRequest.latest_estimate.message && (
                                                                <p className="mt-2 text-sm font-medium leading-relaxed text-[#414141]">{selectedRequest.latest_estimate.message}</p>
                                                            )}
                                                            <EstimateDisplayLines estimate={selectedRequest.latest_estimate} />
                                                        </>
                                                    ) : (
                                                        <p className="text-sm font-medium leading-relaxed text-[#414141]">
                                                            No estimate has been sent for this lead.
                                                        </p>
                                                    )}
                                                </DetailSection>
                                                {(!selectedRequest.latest_estimate || editingEstimateRequestId === selectedRequest.request_id) && (
                                                    <div ref={estimateEditorRef} tabIndex={-1}>
                                                        <DetailSection title={selectedRequest.latest_estimate ? 'Edit estimate' : 'Estimate builder'} className="shadow-none">
                                                            <EstimateBuilder
                                                                request={selectedRequest}
                                                                onSent={handleEstimateSent}
                                                                isEditing={Boolean(selectedRequest.latest_estimate)}
                                                                onCancelEdit={() => setEditingEstimateRequestId('')}
                                                            />
                                                        </DetailSection>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {activeDetailTab === 'service_notes' && (
                                            <ServiceNotesPanel
                                                request={selectedRequest}
                                                onUpdated={handleServiceNotesUpdated}
                                            />
                                        )}

                                        {activeDetailTab === 'messages' && (
                                            <PartnerRequestMessages
                                                request={selectedRequest}
                                                messages={selectedRequestMessages}
                                                currentRole="shop"
                                                title="Messages"
                                                emptyText="No messages with this driver yet."
                                                placeholder="Reply to the driver..."
                                                onSend={handleShopMessage}
                                                onStartService={() => handleStatus('in_service')}
                                                onMarkReadyForPickup={() => handleStatus('ready_for_pickup')}
                                                onConfirmPickup={() => handleStatus('picked_up')}
                                                isUpdatingVisit={isUpdating}
                                                isUpdatingService={isUpdating}
                                                variant="chat"
                                            />
                                        )}

                                        {activeDetailTab === 'activity' && (
                                            <DetailSection title="Activity" className="shadow-none">
                                                <Timeline items={selectedRequest.timeline || []} />
                                            </DetailSection>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </main>
        <ShopReferralInviteModal
            open={isReferralInviteOpen}
            onClose={() => setIsReferralInviteOpen(false)}
            requests={requests}
            referrals={referrals}
            selectedPartnerShopId={selectedAdminShopId}
            requiresShopSelection={isDemoAdminView}
        />
        {isDeclineModalOpen && (
            <DeclineLeadModal
                request={selectedRequest}
                reason={declineReason}
                details={declineDetails}
                error={declineError}
                isSubmitting={isUpdating}
                onReasonChange={(value) => {
                    setDeclineReason(value);
                    setDeclineError('');
                }}
                onDetailsChange={setDeclineDetails}
                onCancel={() => {
                    if (isUpdating) return;
                    setIsDeclineModalOpen(false);
                    setDeclineReason('');
                    setDeclineDetails('');
                    setDeclineError('');
                }}
                onSubmit={handleSubmitDecline}
            />
        )}
        </>
    );
}
